// Dourado Portuguese data + conjugation engines.
// European Portuguese (pt-PT). Authored for Dourado.
//
// Portuguese Verbs Database
// Top 50 verbs include emoji illustrations and explicit irregular conjugations.
// Additional verbs follow regular patterns or have entries in the irregular map.
// The conjugation engine handles regular -ar/-er/-ir patterns automatically and
// derives the synthetic tenses (pluperfect, imperfect & future subjunctive,
// personal infinitive) from the preterite / infinitive.

const TOP_50_VERBS = [
  { inf: "ser", en: "to be (essence)", emoji: "🌟", group: "irregular", note: "Identity, origin, time, permanent traits" },
  { inf: "estar", en: "to be (state)", emoji: "📍", group: "irregular", note: "Location, temporary state; estar a + inf = progressive" },
  { inf: "ter", en: "to have", emoji: "🤲", group: "irregular", note: "Possession; ter de/que = to have to" },
  { inf: "fazer", en: "to do / to make", emoji: "🔨", group: "irregular", note: "Weather: faz frio; particípio: feito" },
  { inf: "poder", en: "to be able / can", emoji: "💪", group: "irregular", note: "Irregular preterite: pude, pôde" },
  { inf: "dizer", en: "to say / to tell", emoji: "💬", group: "irregular", note: "Future: direi; particípio: dito" },
  { inf: "ir", en: "to go", emoji: "🚶", group: "irregular", note: "Shares its preterite with ser (fui, foi)" },
  { inf: "ver", en: "to see", emoji: "👁️", group: "irregular", note: "Particípio: visto; presente: vejo, vês, vê" },
  { inf: "dar", en: "to give", emoji: "🎁", group: "irregular", note: "Presente: dou, dás, dá; conjuntivo: dê" },
  { inf: "saber", en: "to know (facts)", emoji: "🧠", group: "irregular", note: "Knowledge of facts/skills; eu sei" },
  { inf: "querer", en: "to want / to love", emoji: "❤️", group: "irregular", note: "Preterite: quis; conjuntivo: queira" },
  { inf: "chegar", en: "to arrive", emoji: "🛬", group: "regular", note: "Spelling: g→gu before e (conjuntivo: chegue)" },
  { inf: "passar", en: "to pass / to happen", emoji: "⏭️", group: "regular", note: "Fully regular -ar verb" },
  { inf: "dever", en: "must / should", emoji: "📋", group: "regular", note: "Obligation; also 'to owe'" },
  { inf: "ficar", en: "to stay / to become", emoji: "🪑", group: "regular", note: "Spelling: c→qu before e (fique); often replaces estar" },
  { inf: "achar", en: "to think / to find", emoji: "🤔", group: "regular", note: "Opinion: acho que… = I think that…" },
  { inf: "falar", en: "to speak / to talk", emoji: "🗣️", group: "regular", note: "Model regular -ar verb" },
  { inf: "levar", en: "to carry / to take", emoji: "🎒", group: "regular", note: "To take something/someone somewhere" },
  { inf: "deixar", en: "to leave / to let", emoji: "👋", group: "regular", note: "Deixar de + inf = to stop doing" },
  { inf: "seguir", en: "to follow / to continue", emoji: "👣", group: "stem-change", note: "e→i: sigo; conjuntivo: siga" },
  { inf: "encontrar", en: "to find / to meet", emoji: "🔍", group: "regular", note: "Fully regular -ar verb" },
  { inf: "chamar", en: "to call", emoji: "📞", group: "regular", note: "Reflexive: chamar-se (to be named)" },
  { inf: "vir", en: "to come", emoji: "👈", group: "irregular", note: "Presente: venho, vens, vem; gerúndio: vindo" },
  { inf: "pensar", en: "to think", emoji: "💭", group: "regular", note: "Pensar em = to think about" },
  { inf: "sair", en: "to leave / to go out", emoji: "🚪", group: "irregular", note: "Presente: saio, sais, sai" },
  { inf: "voltar", en: "to return", emoji: "🔄", group: "regular", note: "Voltar a + inf = to do again" },
  { inf: "tomar", en: "to take / to drink", emoji: "🥤", group: "regular", note: "Model regular -ar verb" },
  { inf: "conhecer", en: "to know (people/places)", emoji: "🤝", group: "irregular", note: "c→ç: conheço; conjuntivo: conheça" },
  { inf: "viver", en: "to live", emoji: "🏠", group: "regular", note: "Model regular -er verb" },
  { inf: "sentir", en: "to feel", emoji: "💗", group: "stem-change", note: "e→i: sinto; conjuntivo: sinta" },
  { inf: "tentar", en: "to try", emoji: "🎯", group: "regular", note: "Tentar + inf = to try to" },
  { inf: "olhar", en: "to look / to watch", emoji: "👀", group: "regular", note: "Olhar para = to look at" },
  { inf: "contar", en: "to count / to tell", emoji: "🔢", group: "regular", note: "Contar com = to count on" },
  { inf: "começar", en: "to begin / to start", emoji: "🏁", group: "regular", note: "ç→c before e (comece); começar a + inf" },
  { inf: "esperar", en: "to wait / to hope", emoji: "⏳", group: "regular", note: "Two meanings depending on context" },
  { inf: "procurar", en: "to look for", emoji: "🔎", group: "regular", note: "Direct object, no preposition" },
  { inf: "existir", en: "to exist", emoji: "✨", group: "regular", note: "Fully regular -ir verb" },
  { inf: "entrar", en: "to enter", emoji: "🚪", group: "regular", note: "Entrar em = to enter (a place)" },
  { inf: "trabalhar", en: "to work", emoji: "💼", group: "regular", note: "Fully regular -ar verb" },
  { inf: "escrever", en: "to write", emoji: "✍️", group: "irregular", note: "Irregular particípio: escrito" },
  { inf: "perder", en: "to lose / to miss", emoji: "📉", group: "irregular", note: "Presente: perco; conjuntivo: perca" },
  { inf: "receber", en: "to receive", emoji: "📬", group: "regular", note: "c stays soft; fully regular -er verb" },
  { inf: "lembrar", en: "to remember", emoji: "🧩", group: "regular", note: "Lembrar-se de = to remember (reflexive)" },
  { inf: "acabar", en: "to finish / to end", emoji: "🏆", group: "regular", note: "Acabar de + inf = to have just done" },
  { inf: "aceitar", en: "to accept", emoji: "✅", group: "regular", note: "Irregular particípio: aceite" },
  { inf: "parecer", en: "to seem", emoji: "🤨", group: "irregular", note: "c→ç: pareço; conjuntivo: pareça" },
  { inf: "pôr", en: "to put / to place", emoji: "📥", group: "irregular", note: "Presente: ponho, pões, põe; particípio: posto" },
  { inf: "trazer", en: "to bring", emoji: "🎁", group: "irregular", note: "Future: trarei; preterite: trouxe" },
  { inf: "pedir", en: "to ask for / to order", emoji: "🙋", group: "irregular", note: "e→ç: peço; conjuntivo: peça" },
  { inf: "gostar", en: "to like", emoji: "👍", group: "regular", note: "Always takes de: gosto de café" }
];

// Additional common verbs - [ infinitive, English, group ].
// The large frequency-ordered tail is spliced in from the generated dataset;
// this inline seed guarantees a usable set even before that splice.
const ADDITIONAL_VERBS = [
  ["precisar","to need","regular"],["usar","to use","regular"],["mostrar","to show","regular"],
  ["acreditar","to believe","regular"],["conseguir","to manage / to get","stem-change"],["mudar","to change / to move","regular"],
  ["criar","to create / to raise","regular"],["abrir","to open","irregular"],["considerar","to consider","regular"],
  ["ouvir","to hear / to listen","irregular"],["ler","to read","irregular"],["cair","to fall","irregular"],
  ["apresentar","to present / to introduce","regular"],["ganhar","to win / to earn","regular"],["formar","to form","regular"],
  ["partir","to leave / to break","regular"],["morrer","to die","irregular"],["realizar","to carry out","regular"],
  ["compreender","to understand","regular"],["explicar","to explain","regular"],["perguntar","to ask","regular"],
  ["tocar","to touch / to play","regular"],["estudar","to study","regular"],["nascer","to be born","irregular"],
  ["correr","to run","regular"],["pagar","to pay","regular"],["ajudar","to help","regular"],
  ["jogar","to play (a game)","regular"],["escutar","to listen","regular"],["cumprir","to fulfil","regular"],
  ["oferecer","to offer","irregular"],["descobrir","to discover","stem-change"],["levantar","to lift / to raise","regular"],
  ["decidir","to decide","regular"],["repetir","to repeat","stem-change"],["esquecer","to forget","irregular"],
  ["dormir","to sleep","stem-change"],["caminhar","to walk","regular"],["subir","to go up","stem-change"],
  ["mover","to move","regular"],["comer","to eat","regular"],["beber","to drink","regular"],
  ["abraçar","to hug","regular"],["beijar","to kiss","regular"],["amar","to love","regular"],
  ["construir","to build","irregular"],["destruir","to destroy","irregular"],["incluir","to include","irregular"],
  ["aprender","to learn","regular"],["ensinar","to teach","regular"],["preparar","to prepare","regular"],
  ["preferir","to prefer","stem-change"],["mentir","to lie","stem-change"],["fechar","to close","regular"],
  ["sonhar","to dream","regular"],["cozinhar","to cook","regular"],["dançar","to dance","regular"],
  ["cantar","to sing","regular"],["viajar","to travel","regular"],["visitar","to visit","regular"],
  ["chover","to rain","regular"],["nevar","to snow","regular"],["nadar","to swim","regular"],
  ["voar","to fly","regular"],["lavar","to wash","regular"],["limpar","to clean","regular"],
  ["comprar","to buy","regular"],["vender","to sell","regular"],["enviar","to send","regular"],
  ["servir","to serve","stem-change"],["dirigir","to direct / to drive","stem-change"],["proteger","to protect","stem-change"],
  ["conduzir","to drive / to lead","irregular"],["produzir","to produce","irregular"],["traduzir","to translate","irregular"],
  ["rir","to laugh","irregular"],["sorrir","to smile","irregular"],["chorar","to cry","regular"],
  ["medir","to measure","stem-change"],["vestir","to dress","stem-change"],["fugir","to flee","stem-change"],
  ["acordar","to wake up","regular"],["adormecer","to fall asleep","regular"],["despertar","to awaken","regular"],
  ["deitar","to lie down","regular"],["descansar","to rest","regular"],["repousar","to rest","regular"],
  ["relaxar","to relax","regular"],["ressonar","to snore","regular"],["bocejar","to yawn","regular"],
  ["espreguiçar","to stretch oneself","regular"],["banhar","to bathe","regular"],["escovar","to brush","regular"],
  ["pentear","to comb","regular"],["barbear","to shave","regular"],["maquilhar","to put on makeup","regular"],
  ["despir","to undress","stem-change"],["calçar","to put on shoes","regular"],["descalçar","to take off shoes","regular"],
  ["arranjar","to arrange","regular"],["arrumar","to tidy up","regular"],["organizar","to organize","regular"],
  ["planear","to plan","regular"],["agendar","to schedule","regular"],["combinar","to arrange","regular"],
  ["marcar","to book","regular"],["cumprimentar","to greet","regular"],["saudar","to greet","regular"],
  ["acenar","to wave","regular"],["despedir","to say goodbye","stem-change"],["convidar","to invite","regular"],
  ["hospedar","to host","regular"],["acolher","to welcome","regular"],["conversar","to converse","regular"],
  ["telefonar","to telephone","regular"],["faltar","to be lacking","regular"],["comparecer","to attend","regular"],
  ["participar","to participate","regular"],["apressar","to hurry","regular"],["atrasar","to delay","regular"],
  ["demorar","to take long","regular"],["adiar","to postpone","regular"],["habituar","to get used to","regular"],
  ["acostumar","to accustom","regular"],["acontecer","to happen","regular"],["suceder","to happen","regular"],
  ["ocorrer","to occur","regular"],["festejar","to celebrate","regular"],["comemorar","to commemorate","regular"],
  ["celebrar","to celebrate","regular"],["brindar","to toast","regular"],["folgar","to have time off","regular"],
  ["passear","to stroll","regular"],["brincar","to play","regular"],["namorar","to date","regular"],
  ["casar","to marry","regular"],["divorciar","to divorce","regular"],["batizar","to baptize","regular"],
  ["crescer","to grow","regular"],["envelhecer","to grow old","regular"],["falecer","to pass away","regular"],
  ["enterrar","to bury","regular"],["gargalhar","to guffaw","regular"],["dormitar","to doze","regular"],
  ["acariciar","to caress","regular"],["embalar","to lull","regular"],["amamentar","to breastfeed","regular"],
  ["educar","to educate","regular"],["cuidar","to take care","regular"],["tratar","to treat","regular"],
  ["auxiliar","to assist","regular"],["varrer","to sweep","regular"],["esfregar","to scrub","regular"],
  ["aspirar","to vacuum","regular"],["polir","to polish","stem-change"],["encerar","to wax","regular"],
  ["arejar","to air out","regular"],["ventilar","to ventilate","regular"],["secar","to dry","regular"],
  ["molhar","to wet","regular"],["enxugar","to wipe dry","regular"],["estender","to hang out","regular"],
  ["dobrar","to fold","regular"],["engomar","to iron","regular"],["coser","to sew","regular"],
  ["costurar","to sew","regular"],["remendar","to mend","regular"],["cozer","to boil","regular"],
  ["acender","to switch on","regular"],["apagar","to switch off","regular"],["ligar","to turn on","regular"],
  ["desligar","to turn off","regular"],["trancar","to lock","regular"],["destrancar","to unlock","regular"],
  ["fixar","to fasten","regular"],["pregar","to nail","regular"],["martelar","to hammer","regular"],
  ["aparafusar","to screw","regular"],["desaparafusar","to unscrew","regular"],["serrar","to saw","regular"],
  ["furar","to drill","regular"],["pintar","to paint","regular"],["envernizar","to varnish","regular"],
  ["demolir","to demolish","regular"],["reparar","to repair","regular"],["consertar","to mend","regular"],
  ["montar","to assemble","regular"],["desmontar","to dismantle","regular"],["instalar","to install","regular"],
  ["arrendar","to lease","regular"],["alugar","to rent","regular"],["habitar","to inhabit","regular"],
  ["morar","to reside","regular"],["residir","to reside","regular"],["mobilar","to furnish","regular"],
  ["decorar","to decorate","regular"],["pendurar","to hang up","regular"],["arrastar","to drag","regular"],
  ["empurrar","to push","regular"],["puxar","to pull","regular"],["carregar","to carry","regular"],
  ["descarregar","to unload","regular"],["guardar","to put away","regular"],["despejar","to empty out","regular"],
  ["encher","to fill","regular"],["esvaziar","to empty","regular"],["regar","to water","regular"],
  ["plantar","to plant","regular"],["podar","to prune","regular"],["cavar","to dig","regular"],
  ["semear","to sow","regular"],["colher","to harvest","regular"],["reciclar","to recycle","regular"],
  ["separar","to separate","regular"],["tapar","to cover up","regular"],["destapar","to uncover","regular"],
  ["cobrir","to cover","stem-change"],["forrar","to line","regular"],["estragar","to spoil","regular"],
  ["quebrar","to break","regular"],["rachar","to crack","regular"],["entupir","to clog","regular"],
  ["desentupir","to unclog","regular"],["gotejar","to drip","regular"],["pingar","to drip","regular"],
  ["inundar","to flood","regular"],["vazar","to leak","regular"],["assar","to roast","regular"],
  ["fritar","to fry","regular"],["grelhar","to grill","regular"],["ferver","to boil","regular"],
  ["refogar","to sauté","regular"],["saltear","to sauté","regular"],["estufar","to braise","regular"],
  ["guisar","to stew","regular"],["temperar","to season","regular"],["salgar","to salt","regular"],
  ["adoçar","to sweeten","regular"],["provar","to taste","regular"],["saborear","to savour","regular"],
  ["mastigar","to chew","regular"],["engolir","to swallow","stem-change"],["trincar","to crunch","regular"],
  ["morder","to bite","regular"],["chupar","to suck","regular"],["lamber","to lick","regular"],
  ["descascar","to peel","regular"],["cortar","to cut","regular"],["picar","to chop","regular"],
  ["ralar","to grate","regular"],["fatiar","to slice","regular"],["amassar","to knead","regular"],
  ["bater","to beat","regular"],["mexer","to stir","regular"],["misturar","to mix","regular"],
  ["juntar","to add","regular"],["acrescentar","to add","regular"],["verter","to pour","regular"],
  ["escorrer","to drain","regular"],["coar","to strain","regular"],["peneirar","to sift","regular"],
  ["amolecer","to soften","regular"],["endurecer","to harden","regular"],["derreter","to melt","regular"],
  ["congelar","to freeze","regular"],["descongelar","to defrost","regular"],["arrefecer","to cool","regular"],
  ["aquecer","to heat","regular"],["levedar","to leaven","regular"],["fermentar","to ferment","regular"],
  ["conservar","to preserve","regular"],["enlatar","to can","regular"],["defumar","to smoke food","regular"],
  ["curar","to cure","regular"],["marinar","to marinate","regular"],["rechear","to stuff","regular"],
  ["barrar","to spread","regular"],["untar","to grease","regular"],["polvilhar","to sprinkle","regular"],
  ["flambar","to flambé","regular"],["panar","to bread","regular"],["desossar","to debone","regular"],
  ["escamar","to scale fish","regular"],["jantar","to dine","regular"],["almoçar","to have lunch","regular"],
  ["lanchar","to have a snack","regular"],["petiscar","to nibble","regular"],["degustar","to taste","regular"],
  ["nutrir","to nourish","regular"],["alimentar","to feed","regular"],["jejuar","to fast","regular"],
  ["emagrecer","to lose weight","regular"],["engordar","to gain weight","regular"],["digerir","to digest","stem-change"],
  ["azedar","to turn sour","regular"],["apodrecer","to rot","regular"],["mofar","to grow mouldy","regular"],
  ["treinar","to train","regular"],["praticar","to practise","regular"],["exercitar","to drill","regular"],
  ["rever","to review","irregular"],["memorizar","to memorize","regular"],["anotar","to note down","regular"],
  ["apontar","to jot down","regular"],["sublinhar","to underline","regular"],["resumir","to summarize","regular"],
  ["demonstrar","to demonstrate","regular"],["exemplificar","to exemplify","regular"],["corrigir","to correct","regular"],
  ["avaliar","to evaluate","regular"],["classificar","to grade","regular"],["examinar","to examine","regular"],
  ["testar","to test","regular"],["reprovar","to fail someone","regular"],["aprovar","to approve","regular"],
  ["graduar","to graduate","regular"],["matricular","to enrol","regular"],["inscrever","to sign up","regular"],
  ["frequentar","to attend","regular"],["assistir","to attend","regular"],["lecionar","to lecture","regular"],
  ["palestrar","to give a talk","regular"],["investigar","to investigate","regular"],["pesquisar","to research","regular"],
  ["analisar","to analyse","regular"],["sintetizar","to synthesize","regular"],["concluir","to conclude","regular"],
  ["deduzir","to deduce","regular"],["calcular","to calculate","regular"],["somar","to add up","regular"],
  ["subtrair","to subtract","irregular"],["multiplicar","to multiply","regular"],["dividir","to divide","regular"],
  ["resolver","to solve","regular"],["solucionar","to solve","regular"],["laborar","to labour","regular"],
  ["empregar","to employ","regular"],["contratar","to hire","regular"],["demitir","to dismiss","regular"],
  ["reformar","to retire","regular"],["aposentar","to retire","regular"],["gerir","to manage","stem-change"],
  ["administrar","to administer","regular"],["liderar","to lead","regular"],["chefiar","to head","regular"],
  ["supervisionar","to supervise","regular"],["coordenar","to coordinate","regular"],["delegar","to delegate","regular"],
  ["orientar","to guide","regular"],["aconselhar","to advise","regular"],["negociar","to negotiate","regular"],
  ["assinar","to sign","regular"],["redigir","to draft","regular"],["elaborar","to draw up","regular"],
  ["projetar","to design","regular"],["desenvolver","to develop","regular"],["implementar","to implement","regular"],
  ["executar","to execute","regular"],["desempenhar","to perform a role","regular"],["fabricar","to manufacture","regular"],
  ["fornecer","to supply","regular"],["abastecer","to supply","regular"],["distribuir","to distribute","regular"],
  ["entregar","to deliver","regular"],["encomendar","to order","regular"],["arquivar","to file","regular"],
  ["imprimir","to print","regular"],["fotocopiar","to photocopy","regular"],["preencher","to fill out","regular"],
  ["candidatar","to apply for","regular"],["concorrer","to compete","regular"],["colaborar","to collaborate","regular"],
  ["cooperar","to cooperate","regular"],["reunir","to gather","regular"],["convocar","to summon","regular"],
  ["relatar","to report","regular"],["reportar","to report","regular"],["informar","to inform","regular"],
  ["comunicar","to communicate","regular"],["notificar","to notify","regular"],["guiar","to guide","regular"],
  ["estacionar","to park","regular"],["acelerar","to speed up","regular"],["travar","to brake","regular"],
  ["abrandar","to slow down","regular"],["ultrapassar","to overtake","regular"],["buzinar","to honk","regular"],
  ["embarcar","to board","regular"],["desembarcar","to disembark","regular"],["aterrar","to land","regular"],
  ["descolar","to take off","regular"],["atracar","to dock","regular"],["zarpar","to set sail","regular"],
  ["navegar","to sail","regular"],["remar","to row","regular"],["mergulhar","to dive","regular"],
  ["flutuar","to float","regular"],["afundar","to sink","regular"],["naufragar","to be shipwrecked","regular"],
  ["andar","to walk","regular"],["saltar","to jump","regular"],["trepar","to climb","regular"],
  ["escalar","to scale","regular"],["descer","to go down","regular"],["atravessar","to cross","regular"],
  ["percorrer","to traverse","regular"],["deslocar","to move","regular"],["transportar","to transport","regular"],
  ["rebocar","to tow","regular"],["arrancar","to pull away","regular"],["parar","to stop","regular"],
  ["regressar","to return","regular"],["retornar","to return","regular"],["aproximar","to approach","regular"],
  ["afastar","to move away","regular"],["vaguear","to wander","regular"],["extraviar","to mislay","regular"],
  ["acampar","to camp","regular"],["pernoitar","to spend the night","regular"],["alojar","to lodge","regular"],
  ["reservar","to reserve","regular"],["cancelar","to cancel","regular"],["confirmar","to confirm","regular"],
  ["remarcar","to rebook","regular"],["explorar","to explore","regular"],["fotografar","to photograph","regular"],
  ["filmar","to film","regular"],["mapear","to map","regular"],["localizar","to locate","regular"],
  ["sinalizar","to signal","regular"],["apitar","to whistle","regular"],["respirar","to breathe","regular"],
  ["inspirar","to inhale","regular"],["expirar","to exhale","regular"],["suspirar","to sigh","regular"],
  ["tossir","to cough","stem-change"],["espirrar","to sneeze","regular"],["assoar","to blow the nose","regular"],
  ["engasgar","to choke","regular"],["sufocar","to suffocate","regular"],["ofegar","to pant","regular"],
  ["transpirar","to perspire","regular"],["suar","to sweat","regular"],["arrepiar","to give chills","regular"],
  ["tremer","to tremble","regular"],["estremecer","to shudder","regular"],["arquejar","to gasp","regular"],
  ["adoecer","to fall ill","regular"],["sarar","to heal","regular"],["medicar","to medicate","regular"],
  ["vacinar","to vaccinate","regular"],["operar","to operate","regular"],["amputar","to amputate","regular"],
  ["suturar","to suture","regular"],["sangrar","to bleed","regular"],["inchar","to swell","regular"],
  ["doer","to hurt","regular"],["arder","to sting","regular"],["latejar","to throb","regular"],
  ["coçar","to scratch","regular"],["arranhar","to scratch","regular"],["ferir","to wound","stem-change"],
  ["magoar","to hurt","regular"],["machucar","to injure","regular"],["aleijar","to maim","regular"],
  ["torcer","to sprain","regular"],["fraturar","to fracture","regular"],["cicatrizar","to scar over","regular"],
  ["recuperar","to recover","regular"],["convalescer","to convalesce","regular"],["desmaiar","to faint","regular"],
  ["desfalecer","to swoon","regular"],["vomitar","to vomit","regular"],["enjoar","to feel sick","regular"],
  ["constipar","to catch a cold","regular"],["alongar","to stretch","regular"],["fortalecer","to strengthen","regular"],
  ["enfraquecer","to weaken","regular"],["cansar","to tire","regular"],["esgotar","to exhaust","regular"],
  ["hidratar","to hydrate","regular"],["desidratar","to dehydrate","regular"],["diagnosticar","to diagnose","regular"],
  ["prescrever","to prescribe","regular"],["internar","to hospitalize","regular"],["parir","to give birth","regular"],
  ["abortar","to miscarry","regular"],["contagiar","to infect","regular"],["contaminar","to contaminate","regular"],
  ["infetar","to infect","regular"],["imunizar","to immunize","regular"],["prevenir","to prevent","regular"],
  ["contrair","to contract illness","irregular"],["piorar","to worsen","regular"],["melhorar","to get better","regular"],
  ["revigorar","to reinvigorate","regular"],["adorar","to adore","regular"],["odiar","to hate","regular"],
  ["detestar","to detest","regular"],["desgostar","to dislike","regular"],["desejar","to wish","regular"],
  ["ansiar","to yearn","regular"],["ambicionar","to crave","regular"],["apetecer","to feel like","regular"],
  ["recear","to fear","regular"],["temer","to dread","regular"],["assustar","to scare","regular"],
  ["amedrontar","to frighten","regular"],["aterrorizar","to terrorize","regular"],["preocupar","to worry","regular"],
  ["inquietar","to disquiet","regular"],["angustiar","to distress","regular"],["afligir","to afflict","regular"],
  ["entristecer","to sadden","regular"],["deprimir","to depress","regular"],["desanimar","to discourage","regular"],
  ["animar","to cheer up","regular"],["alegrar","to gladden","regular"],["contentar","to please","regular"],
  ["satisfazer","to satisfy","irregular"],["agradar","to please","regular"],["desagradar","to displease","regular"],
  ["irritar","to irritate","regular"],["enfurecer","to enrage","regular"],["zangar","to anger","regular"],
  ["chatear","to annoy","regular"],["aborrecer","to bore","regular"],["incomodar","to bother","regular"],
  ["acalmar","to calm","regular"],["sossegar","to settle","regular"],["tranquilizar","to reassure","regular"],
  ["confortar","to comfort","regular"],["consolar","to console","regular"],["encorajar","to encourage","regular"],
  ["estimular","to stimulate","regular"],["motivar","to motivate","regular"],["emocionar","to move emotionally","regular"],
  ["comover","to touch","regular"],["impressionar","to impress","regular"],["surpreender","to surprise","regular"],
  ["espantar","to astonish","regular"],["admirar","to admire","regular"],["invejar","to envy","regular"],
  ["desprezar","to despise","regular"],["humilhar","to humiliate","regular"],["envergonhar","to shame","regular"],
  ["orgulhar","to make proud","regular"],["gabar","to boast","regular"],["elogiar","to praise","regular"],
  ["louvar","to extol","regular"],["admoestar","to admonish","regular"],["repreender","to reprimand","regular"],
  ["censurar","to censure","regular"],["criticar","to criticize","regular"],["acusar","to accuse","regular"],
  ["culpar","to blame","regular"],["perdoar","to forgive","regular"],["desculpar","to excuse","regular"],
  ["agradecer","to thank","regular"],["felicitar","to congratulate","regular"],["prometer","to promise","regular"],
  ["jurar","to swear","regular"],["garantir","to guarantee","regular"],["assegurar","to assure","regular"],
  ["avisar","to warn","regular"],["alertar","to alert","regular"],["ameaçar","to threaten","regular"],
  ["ralhar","to scold","regular"],["gritar","to shout","regular"],["berrar","to bawl","regular"],
  ["sussurrar","to whisper","regular"],["murmurar","to murmur","regular"],["balbuciar","to babble","regular"],
  ["gaguejar","to stutter","regular"],["exclamar","to exclaim","regular"],["declarar","to declare","regular"],
  ["afirmar","to affirm","regular"],["negar","to deny","regular"],["recusar","to refuse","regular"],
  ["rejeitar","to reject","regular"],["admitir","to admit","regular"],["confessar","to confess","regular"],
  ["revelar","to reveal","regular"],["ocultar","to conceal","regular"],["esconder","to hide","regular"],
  ["enganar","to deceive","regular"],["iludir","to delude","regular"],["fingir","to pretend","regular"],
  ["disfarçar","to disguise","regular"],["argumentar","to argue","regular"],["discutir","to discuss","regular"],
  ["debater","to debate","regular"],["dialogar","to dialogue","regular"],["comentar","to comment","regular"],
  ["mencionar","to mention","regular"],["referir","to refer to","stem-change"],["citar","to quote","regular"],
  ["narrar","to narrate","regular"],["descrever","to describe","regular"],["expressar","to express","regular"],
  ["exprimir","to express","regular"],["manifestar","to manifest","regular"],["opinar","to opine","regular"],
  ["sugerir","to suggest","stem-change"],["recomendar","to recommend","regular"],["insinuar","to insinuate","regular"],
  ["indicar","to indicate","regular"],["esclarecer","to clarify","regular"],["interpretar","to interpret","regular"],
  ["soletrar","to spell out","regular"],["pronunciar","to pronounce","regular"],["articular","to articulate","regular"],
  ["calar","to silence","regular"],["silenciar","to silence","regular"],["chuviscar","to drizzle","regular"],
  ["gear","to frost","regular"],["granizar","to hail","regular"],["trovejar","to thunder","regular"],
  ["relampejar","to flash lightning","regular"],["ventar","to be windy","regular"],["soprar","to blow","regular"],
  ["amanhecer","to dawn","regular"],["anoitecer","to grow dark","regular"],["escurecer","to darken","regular"],
  ["clarear","to brighten","regular"],["brilhar","to shine","regular"],["reluzir","to glitter","regular"],
  ["cintilar","to twinkle","regular"],["resplandecer","to glow","regular"],["iluminar","to illuminate","regular"],
  ["ofuscar","to dazzle","regular"],["evaporar","to evaporate","regular"],["condensar","to condense","regular"],
  ["transbordar","to overflow","regular"],["fluir","to flow","regular"],["jorrar","to gush","regular"],
  ["borbulhar","to bubble","regular"],["murchar","to wilt","regular"],["florescer","to flourish","regular"],
  ["florir","to flower","regular"],["brotar","to sprout","regular"],["germinar","to germinate","regular"],
  ["desabrochar","to blossom","regular"],["amadurecer","to ripen","regular"],["proliferar","to proliferate","regular"],
  ["reproduzir","to reproduce","regular"],["migrar","to migrate","regular"],["hibernar","to hibernate","regular"],
  ["caçar","to hunt","regular"],["pescar","to fish","regular"],["rastejar","to crawl","regular"],
  ["escavar","to excavate","regular"],["erodir","to erode","regular"],["desmoronar","to collapse","regular"],
  ["abalar","to shake","regular"],["explodir","to explode","regular"],["implodir","to implode","regular"],
  ["queimar","to burn","regular"],["incendiar","to set fire to","regular"],["incinerar","to incinerate","regular"],
  ["extinguir","to extinguish","regular"],["poluir","to pollute","regular"],["reflorestar","to reforest","regular"],
  ["desflorestar","to deforest","regular"],["cultivar","to cultivate","regular"],["adubar","to fertilize","regular"],
  ["conectar","to connect","regular"],["desconectar","to disconnect","regular"],["clicar","to click","regular"],
  ["baixar","to lower","regular"],["transferir","to transfer","stem-change"],["desinstalar","to uninstall","regular"],
  ["atualizar","to update","regular"],["configurar","to configure","regular"],["formatar","to format","regular"],
  ["reiniciar","to restart","regular"],["programar","to program","regular"],["codificar","to encode","regular"],
  ["descodificar","to decode","regular"],["compilar","to compile","regular"],["depurar","to debug","regular"],
  ["processar","to process","regular"],["armazenar","to store","regular"],["gravar","to record","regular"],
  ["eliminar","to delete","regular"],["copiar","to copy","regular"],["colar","to paste","regular"],
  ["selecionar","to select","regular"],["digitar","to type","regular"],["teclar","to key in","regular"],
  ["digitalizar","to scan","regular"],["filtrar","to filter","regular"],["ordenar","to sort","regular"],
  ["sincronizar","to synchronize","regular"],["partilhar","to share","regular"],["encaminhar","to forward","regular"],
  ["anexar","to attach","regular"],["bloquear","to block","regular"],["desbloquear","to unblock","regular"],
  ["encriptar","to encrypt","regular"],["desencriptar","to decrypt","regular"],["piratear","to hack","regular"],
  ["aceder","to access","regular"],["autenticar","to authenticate","regular"],["registar","to register","regular"],
  ["subscrever","to subscribe","regular"],["ativar","to activate","regular"],["desativar","to deactivate","regular"],
  ["emparelhar","to pair","regular"],["transmitir","to broadcast","regular"],["difundir","to spread","regular"],
  ["simular","to simulate","regular"],["modelar","to model","regular"],["renderizar","to render","regular"],
  ["inovar","to innovate","regular"],["automatizar","to automate","regular"],["otimizar","to optimize","regular"],
  ["monitorizar","to monitor","regular"],["detetar","to detect","regular"],["rastrear","to track","regular"],
  ["revender","to resell","regular"],["cobrar","to charge","regular"],["custar","to cost","regular"],
  ["valer","to be worth","irregular"],["gastar","to spend","regular"],["poupar","to save money","regular"],
  ["economizar","to economize","regular"],["investir","to invest","stem-change"],["lucrar","to profit","regular"],
  ["faturar","to invoice","regular"],["devolver","to return an item","regular"],["reembolsar","to reimburse","regular"],
  ["trocar","to exchange","regular"],["regatear","to haggle","regular"],["pechinchar","to bargain","regular"],
  ["descontar","to discount","regular"],["encarecer","to raise the price","regular"],["baratear","to cheapen","regular"],
  ["emprestar","to lend","regular"],["requisitar","to requisition","regular"],["financiar","to finance","regular"],
  ["endividar","to indebt","regular"],["hipotecar","to mortgage","regular"],["penhorar","to pawn","regular"],
  ["depositar","to deposit","regular"],["sacar","to withdraw","regular"],["remunerar","to remunerate","regular"],
  ["subornar","to bribe","regular"],["extorquir","to extort","regular"],["roubar","to rob","regular"],
  ["furtar","to steal","regular"],["fraudar","to defraud","regular"],["contrabandear","to smuggle","regular"],
  ["falsificar","to forge","regular"],["herdar","to inherit","regular"],["legar","to bequeath","regular"],
  ["doar","to donate","regular"],["presentear","to give gifts","regular"],["repartir","to share out","regular"],
  ["arrecadar","to collect funds","regular"],["embrulhar","to wrap","regular"],["empacotar","to pack","regular"],
  ["desempacotar","to unpack","regular"],["estocar","to stock","regular"],["inventariar","to inventory","regular"],
  ["contabilizar","to account for","regular"],["orçamentar","to budget","regular"],["tributar","to tax","regular"],
  ["taxar","to levy","regular"],["multar","to fine","regular"],["indemnizar","to indemnify","regular"],
  ["compensar","to compensate","regular"],["desenhar","to draw","regular"],["esboçar","to sketch","regular"],
  ["colorir","to colour","regular"],["moldar","to mould","regular"],["esculpir","to sculpt","regular"],
  ["talhar","to carve","regular"],["representar","to act","regular"],["encenar","to stage","regular"],
  ["atuar","to perform","regular"],["bailar","to dance","regular"],["entoar","to intone","regular"],
  ["assobiar","to whistle","regular"],["dedilhar","to strum","regular"],["afinar","to tune","regular"],
  ["improvisar","to improvise","regular"],["ensaiar","to rehearse","regular"],["estrear","to premiere","regular"],
  ["exibir","to exhibit","regular"],["apreciar","to appreciate","regular"],["contemplar","to contemplate","regular"],
  ["recitar","to recite","regular"],["declamar","to declaim","regular"],["rimar","to rhyme","regular"],
  ["publicar","to publish","regular"],["editar","to edit","regular"],["ilustrar","to illustrate","regular"],
  ["folhear","to leaf through","regular"],["reler","to reread","irregular"],["apostar","to bet","regular"],
  ["empatar","to draw a game","regular"],["competir","to compete","stem-change"],["divertir","to amuse","stem-change"],
  ["entreter","to entertain","irregular"],["distrair","to distract","irregular"],["colecionar","to collect","regular"],
  ["tricotar","to knit","regular"],["bordar","to embroider","regular"],["tecer","to weave","regular"],
  ["fundir","to cast","regular"],["soldar","to weld","regular"],["restaurar","to restore","regular"],
  ["caricaturar","to caricature","regular"],["governar","to govern","regular"],["reinar","to reign","regular"],
  ["presidir","to preside","regular"],["comandar","to command","regular"],["mandar","to order","regular"],
  ["legislar","to legislate","regular"],["decretar","to decree","regular"],["promulgar","to promulgate","regular"],
  ["regulamentar","to regulate","regular"],["regular","to regulate","regular"],["proibir","to prohibit","regular"],
  ["permitir","to permit","regular"],["autorizar","to authorize","regular"],["vetar","to veto","regular"],
  ["ratificar","to ratify","regular"],["abolir","to abolish","regular"],["revogar","to repeal","regular"],
  ["emendar","to amend","regular"],["votar","to vote","regular"],["eleger","to elect","regular"],
  ["nomear","to appoint","regular"],["designar","to designate","regular"],["destituir","to depose","regular"],
  ["renunciar","to resign","regular"],["defender","to defend","regular"],["julgar","to judge","regular"],
  ["condenar","to condemn","regular"],["absolver","to acquit","regular"],["sentenciar","to sentence","regular"],
  ["litigar","to litigate","regular"],["recorrer","to appeal","regular"],["apelar","to appeal","regular"],
  ["testemunhar","to testify","regular"],["fiscalizar","to oversee","regular"],["inspecionar","to inspect","regular"],
  ["vigiar","to keep watch","regular"],["patrulhar","to patrol","regular"],["prender","to arrest","regular"],
  ["deter","to detain","irregular"],["soltar","to release","regular"],["libertar","to free","regular"],
  ["exilar","to exile","regular"],["deportar","to deport","regular"],["imigrar","to immigrate","regular"],
  ["emigrar","to emigrate","regular"],["colonizar","to colonize","regular"],["conquistar","to conquer","regular"],
  ["dominar","to dominate","regular"],["oprimir","to oppress","regular"],["revoltar","to revolt","regular"],
  ["rebelar","to rebel","regular"],["protestar","to protest","regular"],["reivindicar","to demand","regular"],
  ["mobilizar","to mobilize","regular"],["unir","to unite","regular"],["mediar","to mediate","regular"],
  ["arbitrar","to arbitrate","regular"],["conciliar","to reconcile","regular"],["pacificar","to pacify","regular"],
  ["combater","to combat","regular"],["lutar","to fight","regular"],["atacar","to attack","regular"],
  ["invadir","to invade","regular"],["ocupar","to occupy","regular"],["retirar","to withdraw","regular"],
  ["render","to surrender","regular"],["capitular","to capitulate","regular"],["aliar","to ally","regular"],
  ["trair","to betray","irregular"],["espiar","to spy","regular"],["sabotar","to sabotage","regular"],
  ["subsidiar","to subsidize","regular"],["privatizar","to privatize","regular"],["nacionalizar","to nationalize","regular"],
  ["modernizar","to modernize","regular"],["civilizar","to civilize","regular"],["integrar","to integrate","regular"],
  ["segregar","to segregate","regular"],["discriminar","to discriminate","regular"],["marginalizar","to marginalize","regular"],
  ["excluir","to exclude","regular"],["pertencer","to belong","regular"],["depender","to depend","regular"],
  ["consistir","to consist","regular"],["constar","to be on record","regular"],["implicar","to imply","regular"],
  ["envolver","to involve","regular"],["conter","to contain","irregular"],["abranger","to encompass","regular"],
  ["entender","to understand","regular"],["perceber","to perceive","regular"],["captar","to grasp","regular"],
  ["notar","to notice","regular"],["observar","to observe","regular"],["ponderar","to ponder","regular"],
  ["refletir","to reflect","stem-change"],["meditar","to meditate","regular"],["raciocinar","to reason","regular"],
  ["imaginar","to imagine","regular"],["assumir","to assume","regular"],["presumir","to presume","regular"],
  ["induzir","to induce","regular"],["inferir","to infer","stem-change"],["comprovar","to corroborate","regular"],
  ["verificar","to verify","regular"],["constatar","to ascertain","regular"],["contestar","to contest","regular"],
  ["refutar","to refute","regular"],["contradizer","to contradict","irregular"],["distinguir","to distinguish","regular"],
  ["diferenciar","to differentiate","regular"],["comparar","to compare","regular"],["relacionar","to relate","regular"],
  ["associar","to associate","regular"],["vincular","to link","regular"],["isolar","to isolate","regular"],
  ["categorizar","to categorize","regular"],["agrupar","to group","regular"],["hierarquizar","to rank","regular"],
  ["priorizar","to prioritize","regular"],["escolher","to choose","regular"],["optar","to opt","regular"],
  ["determinar","to determine","regular"],["definir","to define","regular"],["delimitar","to delimit","regular"],
  ["estabelecer","to establish","regular"],["fundar","to found","regular"],["instituir","to institute","regular"],
  ["gerar","to generate","regular"],["originar","to originate","regular"],["provocar","to provoke","regular"],
  ["causar","to cause","regular"],["acarretar","to entail","regular"],["resultar","to result","regular"],
  ["decorrer","to elapse","regular"],["proceder","to proceed","regular"],["prosseguir","to continue","stem-change"],
  ["prolongar","to prolong","regular"],["suspender","to suspend","regular"],["interromper","to interrupt","regular"],
  ["cessar","to cease","regular"],["terminar","to end","regular"],["finalizar","to finalize","regular"],
  ["completar","to complete","regular"],["alcançar","to attain","regular"],["atingir","to reach","regular"],
  ["falhar","to fail","regular"],["fracassar","to flop","regular"],["prevalecer","to prevail","regular"],
  ["predominar","to predominate","regular"],["sobressair","to stand out","irregular"],["destacar","to highlight","regular"],
  ["salientar","to point out","regular"],["realçar","to highlight","regular"],["enfatizar","to emphasize","regular"],
  ["acentuar","to accentuate","regular"],["intensificar","to intensify","regular"],["aumentar","to increase","regular"],
  ["diminuir","to decrease","regular"],["reduzir","to reduce","regular"],["ampliar","to enlarge","regular"],
  ["expandir","to expand","regular"],["encolher","to shrink","regular"],["estreitar","to narrow","regular"],
  ["alargar","to widen","regular"],["aprofundar","to deepen","regular"],["superar","to overcome","regular"],
  ["exceder","to exceed","regular"],["limitar","to limit","regular"],["restringir","to restrict","regular"],
  ["condicionar","to condition","regular"],["possibilitar","to enable","regular"],["viabilizar","to make feasible","regular"],
  ["facilitar","to facilitate","regular"],["dificultar","to hinder","regular"],["impedir","to prevent","stem-change"],
  ["obstar","to obstruct","regular"],["favorecer","to favour","regular"],["beneficiar","to benefit","regular"],
  ["prejudicar","to harm","regular"],["comprometer","to compromise","regular"],["arriscar","to risk","regular"],
  ["ousar","to dare","regular"],["atrever","to dare","regular"],["almejar","to long for","regular"],
  ["anelar","to yearn for","regular"],["acalentar","to cherish","regular"],["enaltecer","to extol","regular"],
  ["exaltar","to exalt","regular"],["glorificar","to glorify","regular"],["venerar","to venerate","regular"],
  ["reverenciar","to revere","regular"],["idolatrar","to idolize","regular"],["prostrar","to prostrate","regular"],
  ["ajoelhar","to kneel","regular"],["curvar","to bow","regular"],["vergar","to bend","regular"],
  ["inclinar","to incline","regular"],["suplicar","to implore","regular"],["implorar","to beg","regular"],
  ["rogar","to plead","regular"],["clamar","to clamour","regular"],["bradar","to bellow","regular"],
  ["proclamar","to proclaim","regular"],["apregoar","to cry out wares","regular"],["anunciar","to announce","regular"],
  ["profetizar","to prophesy","regular"],["augurar","to augur","regular"],["pressagiar","to presage","regular"],
  ["prenunciar","to foreshadow","regular"],["vaticinar","to foretell","regular"],["predizer","to predict","irregular"],
  ["prever","to foresee","irregular"],["antever","to anticipate","irregular"],["divisar","to descry","regular"],
  ["vislumbrar","to glimpse","regular"],["entrever","to glimpse","irregular"],["fitar","to stare at","regular"],
  ["mirar","to gaze","regular"],["perscrutar","to scrutinize","regular"],["esquadrinhar","to comb through","regular"],
  ["sondar","to probe","regular"],["indagar","to inquire","regular"],["inquirir","to inquire","regular"],
  ["auscultar","to sound out","regular"],["cogitar","to cogitate","regular"],["matutar","to mull over","regular"],
  ["ruminar","to ruminate","regular"],["divagar","to digress","regular"],["discorrer","to discourse","regular"],
  ["dissertar","to dissertate","regular"],["expender","to expound","regular"],["explanar","to expound","regular"],
  ["elucidar","to elucidate","regular"],["discernir","to discern","regular"],["atinar","to figure out","regular"],
  ["deparar","to come across","regular"],["defrontar","to confront","regular"],["afrontar","to affront","regular"],
  ["arrostar","to brave","regular"],["enfrentar","to face","regular"],["sucumbir","to succumb","regular"],
  ["perecer","to perish","regular"],["fenecer","to wither away","regular"],["definhar","to waste away","regular"],
  ["esmorecer","to lose heart","regular"],["periclitar","to be imperilled","regular"],["soçobrar","to founder","regular"],
  ["malograr","to thwart","regular"],["lograr","to manage to","regular"],["granjear","to win over","regular"],
  ["auferir","to derive","stem-change"],["coligir","to compile","regular"],["compendiar","to summarize","regular"],
  ["esmiuçar","to scrutinize","regular"],["pormenorizar","to detail","regular"],["elencar","to list","regular"],
  ["arrolar","to enrol","regular"],["catalogar","to catalogue","regular"],["consignar","to set down","regular"],
  ["exarar","to record","regular"],["lavrar","to draw up","regular"],["rubricar","to initial","regular"],
  ["chancelar","to endorse","regular"],["referendar","to countersign","regular"],["homologar","to ratify","regular"],
  ["sancionar","to sanction","regular"],["outorgar","to grant","regular"],["conceder","to concede","regular"],
  ["facultar","to provide","regular"],["prover","to provide","irregular"],["suprir","to make up for","regular"],
  ["prescindir","to dispense with","regular"],["carecer","to lack","regular"],["escassear","to grow scarce","regular"],
  ["abundar","to abound","regular"],["pulular","to teem","regular"],["fervilhar","to swarm","regular"],
  ["grassar","to spread","regular"],["alastrar","to spread","regular"],["propagar","to propagate","regular"],
  ["disseminar","to disseminate","regular"],["irradiar","to radiate","regular"],["emanar","to emanate","regular"],
  ["dimanar","to emanate from","regular"],["promanar","to spring from","regular"],["provir","to come from","irregular"],
  ["advir","to ensue","irregular"],["sobrevir","to supervene","irregular"],["redundar","to redound","regular"],
  ["reverter","to revert","regular"],["converter","to convert","regular"],["transmutar","to transmute","regular"],
  ["metamorfosear","to metamorphose","regular"],["transfigurar","to transfigure","regular"],["transtornar","to upset","regular"],
  ["perturbar","to perturb","regular"],["conturbar","to trouble","regular"],["vacilar","to waver","regular"],
  ["hesitar","to hesitate","regular"],["titubear","to falter","regular"],["oscilar","to oscillate","regular"],
  ["pairar","to hover","regular"],["adejar","to flutter","regular"],["esvoaçar","to flit","regular"],
  ["chilrear","to chirp","regular"],["gorjear","to warble","regular"],["trinar","to trill","regular"],
  ["rugir","to roar","regular"],["bramir","to bellow","regular"],["uivar","to howl","regular"],
  ["ladrar","to bark","regular"],["latir","to yelp","regular"],["miar","to meow","regular"],
  ["relinchar","to neigh","regular"],["mugir","to moo","regular"],["balir","to bleat","regular"],
  ["grasnar","to caw","regular"],["coaxar","to croak","regular"],["zumbir","to buzz","regular"],
  ["ciciar","to whisper softly","regular"],["farfalhar","to rustle","regular"],["ranger","to creak","regular"],
  ["chiar","to squeak","regular"],["estalar","to crackle","regular"],["crepitar","to crackle","regular"],
  ["retumbar","to resound","regular"],["ecoar","to echo","regular"],["ressoar","to resound","regular"],
  ["reboar","to reverberate","regular"],["estrondear","to boom","regular"],["abafar","to muffle","regular"],
  ["abanar","to shake","regular"],["abarcar","to encompass","regular"],["abater","to knock down","regular"],
  ["abdicar","to abdicate","regular"],["abotoar","to button","regular"],["desabotoar","to unbutton","regular"],
  ["abreviar","to abbreviate","regular"],["abrigar","to shelter","regular"],["absorver","to absorb","regular"],
  ["acatar","to comply with","regular"],["acertar","to get right","regular"],["achatar","to flatten","regular"],
  ["acionar","to trigger","regular"],["aclamar","to acclaim","regular"],["aclarar","to clear up","regular"],
  ["acometer","to assail","regular"],["acomodar","to accommodate","regular"],["acompanhar","to accompany","regular"],
  ["aconchegar","to snuggle","regular"],["acoplar","to couple","regular"],["acorrentar","to chain","regular"],
  ["acossar","to hound","regular"],["acotovelar","to elbow","regular"],["adaptar","to adapt","regular"],
  ["adentrar","to go into","regular"],["adiantar","to bring forward","regular"],["adicionar","to add","regular"],
  ["adornar","to adorn","regular"],["adquirir","to acquire","regular"],["adular","to flatter","regular"],
  ["advogar","to advocate","regular"],["afagar","to caress","regular"],["afiar","to sharpen","regular"],
  ["afogar","to drown","regular"],["afrouxar","to loosen","regular"],["afugentar","to drive away","regular"],
  ["agarrar","to grab","regular"],["agasalhar","to bundle up","regular"],["agachar","to crouch","regular"],
  ["agitar","to shake","regular"],["agravar","to aggravate","regular"],["agregar","to aggregate","regular"],
  ["aguardar","to await","regular"],["aguentar","to endure","regular"],["aguçar","to sharpen","regular"],
  ["ajeitar","to arrange","regular"],["ajuntar","to gather","regular"],["ajustar","to adjust","regular"],
  ["alegar","to allege","regular"],["algemar","to handcuff","regular"],["alinhar","to align","regular"],
  ["alisar","to smooth","regular"],["alistar","to enlist","regular"],["aliviar","to relieve","regular"],
  ["alternar","to alternate","regular"],["aludir","to allude","regular"],["alumiar","to light up","regular"],
  ["amaciar","to soften","regular"],["amainar","to abate","regular"],["amaldiçoar","to curse","regular"],
  ["amansar","to tame","regular"],["amarrar","to tie up","regular"],["amarrotar","to crumple","regular"],
  ["amenizar","to ease","regular"],["amolar","to sharpen","regular"],["amontoar","to pile up","regular"],
  ["amordaçar","to gag","regular"],["amortecer","to cushion","regular"],["amortizar","to amortize","regular"],
  ["amparar","to support","regular"],["ancorar","to anchor","regular"],["anestesiar","to anaesthetize","regular"],
  ["angariar","to raise funds","regular"],["aniquilar","to annihilate","regular"],["antecipar","to anticipate","regular"],
  ["anteceder","to precede","regular"],["anuir","to assent","regular"],["anular","to annul","regular"],
  ["apalpar","to feel","regular"],["apanhar","to catch","regular"],["aparar","to trim","regular"],
  ["aparecer","to appear","regular"],["aparentar","to seem","regular"],["apartar","to part","regular"],
  ["apavorar","to terrify","regular"],["apedrejar","to stone","regular"],["apelidar","to nickname","regular"],
  ["apreender","to apprehend","regular"],["aprimorar","to refine","regular"],["apoiar","to support","regular"],
  ["aproveitar","to take advantage of","regular"],["apunhalar","to stab","regular"],["apurar","to refine","regular"],
  ["arar","to plough","regular"],["arcar","to bear","regular"],["arfar","to pant","regular"],
  ["armar","to arm","regular"],["arrasar","to raze","regular"],["arrebatar","to snatch","regular"],
  ["arrebentar","to burst","regular"],["arredar","to move aside","regular"],["arregaçar","to roll up","regular"],
  ["arrematar","to finish off","regular"],["arremessar","to hurl","regular"],["arrepender","to repent","regular"],
  ["arrojar","to fling","regular"],["arrombar","to break in","regular"],["arrotar","to burp","regular"],
  ["arruinar","to ruin","regular"],["asfixiar","to asphyxiate","regular"],["assaltar","to mug","regular"],
  ["assassinar","to assassinate","regular"],["assediar","to harass","regular"],["assemelhar","to resemble","regular"],
  ["assentar","to settle","regular"],["assinalar","to mark","regular"],["assomar","to loom","regular"],
  ["assombrar","to haunt","regular"],["atar","to tie","regular"],["atear","to kindle","regular"],
  ["atender","to attend to","regular"],["atentar","to heed","regular"],["atenuar","to attenuate","regular"],
  ["atestar","to attest","regular"],["atiçar","to stoke","regular"],["atirar","to throw","regular"],
  ["atolar","to bog down","regular"],["atordoar","to stun","regular"],["atormentar","to torment","regular"],
  ["atrapalhar","to hinder","regular"],["atribuir","to attribute","regular"],["atropelar","to run over","regular"],
  ["aturar","to put up with","regular"],["aturdir","to bewilder","regular"],["avançar","to advance","regular"],
  ["avistar","to sight","regular"],["baralhar","to shuffle","regular"],["bambolear","to wobble","regular"],
  ["benzer","to bless","regular"],["brigar","to quarrel","regular"],["ceder","to yield","regular"],
  ["cercar","to surround","regular"],["circular","to circulate","regular"],["coabitar","to cohabit","regular"],
  ["debruçar","to lean over","regular"],["declinar","to decline","regular"],["deslizar","to slide","regular"],
  ["despenhar","to plunge","regular"],["difamar","to defame","regular"],["encaixar","to fit in","regular"],
  ["engendrar","to engender","regular"],["enquadrar","to frame","regular"],["esmagar","to crush","regular"],
  ["estancar","to staunch","regular"],["franzir","to frown","regular"],["inflamar","to inflame","regular"],
  ["lapidar","to polish a gem","regular"],["nivelar","to level","regular"],["retorquir","to retort","regular"],
  ["sublevar","to incite revolt","regular"],["tropeçar","to trip","regular"],["vincar","to crease","regular"],
  ["queixar-se","to complain","regular"],["lembrar-se","to remember","regular"],["sentar-se","to sit down","regular"],
  ["levantar-se","to get up","regular"],["deitar-se","to go to bed","regular"],["vestir-se","to get dressed","stem-change"],
  ["despedir-se","to say goodbye","stem-change"],["divertir-se","to have fun","stem-change"],["continuar","to continue","regular"],
  ["iniciar","to begin","regular"],["principiar","to begin","regular"],["recomeçar","to restart","regular"],
  ["retomar","to resume","regular"],["reatar","to resume","regular"],["findar","to end","regular"],
  ["ultimar","to finalize","regular"],["encerrar","to close","regular"],["pausar","to pause","regular"],
  ["pegar","to grab","regular"],["tirar","to take out","regular"],["meter","to put in","regular"],
  ["colocar","to place","regular"],["pousar","to set down","regular"],["encostar","to lean against","regular"],
  ["matar","to kill","regular"],["salvar","to save","regular"],["vencer","to win","regular"],
  ["experimentar","to try","regular"],["durar","to last","regular"],["perdurar","to endure","regular"],
  ["permanecer","to remain","regular"],["subsistir","to subsist","regular"],["persistir","to persist","regular"],
  ["insistir","to insist","regular"],["desistir","to give up","regular"],["resistir","to resist","regular"],
  ["coexistir","to coexist","regular"],["costumar","to be accustomed to","regular"],["pretender","to intend","regular"],
  ["tencionar","to intend","regular"],["aprontar","to get ready","regular"],["tardar","to delay","regular"],
  ["madrugar","to rise early","regular"],["recordar","to recall","regular"],["relembrar","to remind","regular"],
  ["rememorar","to reminisce","regular"],["evocar","to evoke","regular"],["duvidar","to doubt","regular"],
  ["confiar","to trust","regular"],["desconfiar","to distrust","regular"],["suspeitar","to suspect","regular"],
  ["reclamar","to complain","regular"],["exigir","to demand","regular"],["suportar","to bear","regular"],
  ["tolerar","to tolerate","regular"],["obedecer","to obey","regular"],["desobedecer","to disobey","regular"],
  ["respeitar","to respect","regular"],["desrespeitar","to disrespect","regular"],["desacatar","to defy","regular"],
  ["desfrutar","to enjoy","regular"],["usufruir","to benefit from","regular"],["gozar","to enjoy","regular"],
  ["curtir","to enjoy","regular"],["agir","to act","regular"],["reagir","to react","regular"],
  ["afetar","to affect","regular"],["impactar","to impact","regular"],["influenciar","to influence","regular"],
  ["influir","to influence","regular"],["incidir","to bear on","regular"],["repercutir","to reverberate","regular"],
  ["derivar","to derive","regular"],["ocasionar","to bring about","regular"],["zelar","to look after","regular"],
  ["velar","to keep watch","regular"],["vigorar","to be in effect","regular"],["compartilhar","to share","regular"],
  ["escorregar","to slip","regular"],["resvalar","to slide","regular"],["gatinhar","to crawl","regular"],
  ["saltitar","to hop","regular"],["pular","to jump","regular"],["ressaltar","to rebound","regular"],
  ["galgar","to leap over","regular"],["vadear","to wade","regular"],["contornar","to go around","regular"],
  ["rodear","to surround","regular"],["circundar","to encircle","regular"],["rondar","to prowl","regular"],
  ["perambular","to wander","regular"],["deambular","to stroll","regular"],["vagar","to roam","regular"],
  ["zanzar","to wander about","regular"],["errar","to wander","regular"],["recuar","to back up","regular"],
  ["coxear","to limp","regular"],["mancar","to limp","regular"],["claudicar","to limp","regular"],
  ["cambalear","to stagger","regular"],["gingar","to sway","regular"],["rebolar","to wiggle","regular"],
  ["contorcer","to contort","regular"],["retorcer","to twist","regular"],["esbarrar","to bump into","regular"],
  ["roçar","to brush against","regular"],["espalhar","to scatter","regular"],["esparramar","to sprawl","regular"],
  ["dispersar","to disperse","regular"],["içar","to hoist","regular"],["guindar","to hoist","regular"],
  ["alavancar","to leverage","regular"],["empilhar","to stack","regular"],["enfiar","to thread","regular"],
  ["transladar","to relocate","regular"],["transitar","to transit","regular"],["aportar","to reach port","regular"],
  ["velejar","to sail","regular"],["pilotar","to pilot","regular"],["descarrilar","to derail","regular"],
  ["colidir","to collide","regular"],["abalroar","to ram","regular"],["capotar","to overturn","regular"],
  ["derrapar","to skid","regular"],["engarrafar","to bottle","regular"],["atrelar","to hitch","regular"],
  ["desatrelar","to unhitch","regular"],["escaldar","to scald","regular"],["escalfar","to poach","regular"],
  ["gratinar","to gratin","regular"],["tostar","to toast","regular"],["torrar","to roast","regular"],
  ["sovar","to knead","regular"],["desfiar","to shred","regular"],["triturar","to grind","regular"],
  ["caramelizar","to caramelize","regular"],["salpicar","to sprinkle","regular"],["besuntar","to grease","regular"],
  ["requentar","to reheat","regular"],["reidratar","to rehydrate","regular"],["amanteigar","to butter","regular"],
  ["demolhar","to soak","regular"],["ensopar","to soak","regular"],["encharcar","to drench","regular"],
  ["engrossar","to thicken","regular"],["adelgaçar","to thin out","regular"],["coalhar","to curdle","regular"],
  ["apimentar","to season with pepper","regular"],["espetar","to skewer","regular"],["empanar","to coat in breadcrumbs","regular"],
  ["depenar","to pluck","regular"],["estripar","to gut","regular"],["mariscar","to gather shellfish","regular"],
  ["borrifar","to spray","regular"],["guarnecer","to garnish","regular"],["recozer","to overcook","regular"],
  ["esfarelar","to crumble","regular"],["amargar","to make bitter","regular"],["aplainar","to plane","regular"],
  ["lixar","to sand","regular"],["pincelar","to brush on","regular"],["rebitar","to rivet","regular"],
  ["entalhar","to carve","regular"],["cinzelar","to chisel","regular"],["forjar","to forge","regular"],
  ["fiar","to spin thread","regular"],["urdir","to warp","regular"],["alinhavar","to baste","regular"],
  ["encadernar","to bind books","regular"],["emoldurar","to frame","regular"],["estofar","to upholster","regular"],
  ["envidraçar","to glaze","regular"],["ladrilhar","to tile","regular"],["azulejar","to tile with azulejos","regular"],
  ["caiar","to whitewash","regular"],["estucar","to stucco","regular"],["tornear","to turn on a lathe","regular"],
  ["limar","to file","regular"],["lustrar","to polish","regular"],["engraxar","to polish shoes","regular"],
  ["espanar","to dust","regular"],["arear","to scour","regular"],["branquear","to whiten","regular"],
  ["alvejar","to bleach","regular"],["estampar","to stamp","regular"],["rascunhar","to sketch","regular"],
  ["legendar","to subtitle","regular"],["manusear","to handle","regular"],["estipular","to stipulate","regular"],
  ["pactuar","to make a pact","regular"],["transigir","to compromise","regular"],["caucionar","to give surety","regular"],
  ["abonar","to vouch for","regular"],["creditar","to credit","regular"],["debitar","to debit","regular"],
  ["depreciar","to depreciate","regular"],["valorizar","to add value","regular"],["desvalorizar","to devalue","regular"],
  ["cotar","to quote a price","regular"],["leiloar","to auction","regular"],["licitar","to bid","regular"],
  ["estornar","to reverse a transaction","regular"],["liquidar","to settle","regular"],["saldar","to settle a debt","regular"],
  ["quitar","to pay off","regular"],["onerar","to burden","regular"],["coletar","to collect taxes","regular"],
  ["sonegar","to evade tax","regular"],["defraudar","to defraud","regular"],["usurpar","to usurp","regular"],
  ["caluniar","to slander","regular"],["injuriar","to insult","regular"],["intimar","to summon","regular"],
  ["impetrar","to petition","regular"],["impugnar","to challenge","regular"],["legalizar","to legalize","regular"],
  ["criminalizar","to criminalize","regular"],["penalizar","to penalize","regular"],["incriminar","to incriminate","regular"],
  ["inculpar","to inculpate","regular"],["interditar","to forbid","regular"],["deliberar","to deliberate","regular"],
  ["auditar","to audit","regular"],["vistoriar","to inspect","regular"],["escriturar","to keep books","regular"],
  ["minutar","to draft","regular"],["rasurar","to cross out","regular"],["rabiscar","to scribble","regular"],
  ["exonerar","to dismiss from office","regular"],["empossar","to swear into office","regular"],["recensear","to take a census","regular"],
  ["diligenciar","to take steps","regular"],["tramitar","to be processed","regular"],["despachar","to dispatch","regular"],
  ["protocolar","to register officially","regular"],["carimbar","to stamp","regular"],["certificar","to certify","regular"],
  ["legitimar","to legitimize","regular"],["prosperar","to prosper","regular"],["falir","to go bankrupt","regular"],
  ["aplicar","to apply","regular"],["despender","to spend","regular"],["desembolsar","to disburse","regular"],
  ["desperdiçar","to waste","regular"],["esbanjar","to squander","regular"],["dilapidar","to squander","regular"],
  ["estimar","to estimate","regular"],["orçar","to budget","regular"],["tarifar","to set a tariff","regular"],
  ["acumular","to accumulate","regular"],["amealhar","to save up","regular"],["entesourar","to hoard","regular"],
  ["gratificar","to reward","regular"],["recompensar","to reward","regular"],["premiar","to award a prize","regular"],
  ["galardoar","to award","regular"],["patrocinar","to sponsor","regular"],["custear","to fund","regular"],
  ["computar","to compute","regular"],["totalizar","to total","regular"],["esterilizar","to sterilize","regular"],
  ["desinfetar","to disinfect","regular"],["cauterizar","to cauterize","regular"],["entorpecer","to numb","regular"],
  ["transfundir","to transfuse","regular"],["inocular","to inoculate","regular"],["regenerar","to regenerate","regular"],
  ["debilitar","to weaken","regular"],["receitar","to prescribe","regular"],["ministrar","to administer","regular"],
  ["transplantar","to transplant","regular"],["radiografar","to x-ray","regular"],["pulsar","to pulse","regular"],
  ["palpitar","to throb","regular"],["inalar","to inhale","regular"],["exalar","to exhale","regular"],
  ["excretar","to excrete","regular"],["secretar","to secrete","regular"],["coagular","to coagulate","regular"],
  ["diluir","to dilute","regular"],["destilar","to distil","regular"],["decantar","to decant","regular"],
  ["precipitar","to precipitate","regular"],["oxidar","to oxidize","regular"],["dissolver","to dissolve","regular"],
  ["catalisar","to catalyse","regular"],["calibrar","to calibrate","regular"],["mensurar","to measure","regular"],
  ["quantificar","to quantify","regular"],["gravitar","to gravitate","regular"],["orbitar","to orbit","regular"],
  ["propulsar","to propel","regular"],["magnetizar","to magnetize","regular"],["eletrificar","to electrify","regular"],
  ["ionizar","to ionize","regular"],["refratar","to refract","regular"],["polinizar","to pollinate","regular"],
  ["clonar","to clone","regular"],["sequenciar","to sequence","regular"],["pasteurizar","to pasteurize","regular"],
  ["fertilizar","to fertilize","regular"],["cristalizar","to crystallize","regular"],["vaporizar","to vaporize","regular"],
  ["solidificar","to solidify","regular"],["conceber","to conceive","regular"],["engravidar","to become pregnant","regular"],
  ["gestar","to gestate","regular"],["lactar","to lactate","regular"],["desmamar","to wean","regular"],
  ["salivar","to salivate","regular"],["babar","to drool","regular"],["urinar","to urinate","regular"],
  ["defecar","to defecate","regular"],["evacuar","to evacuate","regular"],["menstruar","to menstruate","regular"],
  ["ovular","to ovulate","regular"],["pesar","to weigh","regular"],["concentrar","to concentrate","regular"],
  ["saturar","to saturate","regular"],["impregnar","to impregnate","regular"],["soluçar","to hiccup","regular"],
  ["regurgitar","to regurgitate","regular"],["escarrar","to expectorate","regular"],["reconfigurar","to reconfigure","regular"],
  ["inicializar","to initialize","regular"],["comprimir","to compress","regular"],["descomprimir","to decompress","regular"],
  ["reimprimir","to reprint","regular"],["replicar","to replicate","regular"],["duplicar","to duplicate","regular"],
  ["triplicar","to triple","regular"],["robotizar","to robotize","regular"],["informatizar","to computerize","regular"],
  ["virtualizar","to virtualize","regular"],["indexar","to index","regular"],["consultar","to consult","regular"],
  ["validar","to validate","regular"],["parametrizar","to parameterize","regular"],["customizar","to customize","regular"],
  ["personalizar","to personalize","regular"],["comutar","to switch","regular"],["redirecionar","to redirect","regular"],
  ["reencaminhar","to forward","regular"],["emitir","to emit","regular"],["reconstruir","to rebuild","regular"],
  ["alocar","to allocate","regular"],["realocar","to reallocate","regular"],["reescrever","to rewrite","regular"],
  ["republicar","to republish","regular"],["reeditar","to reissue","regular"],["reorganizar","to reorganize","regular"],
  ["reestruturar","to restructure","regular"],["readaptar","to readapt","regular"],["reajustar","to readjust","regular"],
  ["reavaliar","to reassess","regular"],["reformular","to reformulate","regular"],["formular","to formulate","regular"],
  ["recalcular","to recalculate","regular"],["reconsiderar","to reconsider","regular"],["remodelar","to remodel","regular"],
  ["renovar","to renew","regular"],["reabilitar","to rehabilitate","regular"],["reativar","to reactivate","regular"],
  ["reanimar","to revive","regular"],["reavivar","to rekindle","regular"],["avivar","to brighten","regular"],
  ["reintegrar","to reintegrate","regular"],["ressuscitar","to resuscitate","regular"],["restabelecer","to reestablish","regular"],
  ["enxertar","to graft","regular"],["replantar","to replant","regular"],["mondar","to weed","regular"],
  ["capinar","to hoe weeds","regular"],["ceifar","to reap","regular"],["debulhar","to thresh","regular"],
  ["ordenhar","to milk","regular"],["pastar","to graze","regular"],["apascentar","to pasture","regular"],
  ["pastorear","to shepherd","regular"],["domesticar","to domesticate","regular"],["amestrar","to train animals","regular"],
  ["adestrar","to tame","regular"],["domar","to break in","regular"],["engaiolar","to cage","regular"],
  ["enjaular","to cage","regular"],["chocar","to hatch","regular"],["desovar","to spawn","regular"],
  ["acasalar","to mate","regular"],["procriar","to procreate","regular"],["frutificar","to bear fruit","regular"],
  ["vingar","to thrive","regular"],["arborizar","to plant with trees","regular"],["desmatar","to clear land","regular"],
  ["irrigar","to irrigate","regular"],["drenar","to drain","regular"],["represar","to dam","regular"],
  ["escoar","to drain off","regular"],["soterrar","to bury","regular"],["rarear","to thin out","regular"],
  ["escampar","to clear up","regular"],["estiar","to stop raining","regular"],["anuviar","to cloud over","regular"],
  ["enevoar","to fog up","regular"],["toldar","to overcast","regular"],["avermelhar","to redden","regular"],
  ["enraivecer","to enrage","regular"],["exasperar","to exasperate","regular"],["enervar","to irritate","regular"],
  ["encolerizar","to anger","regular"],["amuar","to sulk","regular"],["emburrar","to sulk","regular"],
  ["pasmar","to be astounded","regular"],["maravilhar","to amaze","regular"],["deslumbrar","to dazzle","regular"],
  ["encantar","to enchant","regular"],["enfeitiçar","to bewitch","regular"],["cativar","to captivate","regular"],
  ["fascinar","to fascinate","regular"],["entusiasmar","to excite","regular"],["empolgar","to thrill","regular"],
  ["enternecer","to move emotionally","regular"],["serenar","to calm","regular"],["apaziguar","to appease","regular"],
  ["reconciliar","to reconcile","regular"],["confraternizar","to fraternize","regular"],["simpatizar","to take a liking","regular"],
  ["antipatizar","to dislike","regular"],["repugnar","to disgust","regular"],["enojar","to nauseate","regular"],
  ["nausear","to nauseate","regular"],["entediar","to bore","regular"],["enfastiar","to weary","regular"],
  ["lastimar","to lament","regular"],["lamentar","to lament","regular"],["prantear","to mourn","regular"],
  ["contristar","to sadden","regular"],["consternar","to dismay","regular"],["desolar","to desolate","regular"],
  ["desalentar","to dishearten","regular"],["desencorajar","to discourage","regular"],["intimidar","to intimidate","regular"],
  ["frustrar","to frustrate","regular"],["dececionar","to disappoint","regular"],["desapontar","to disappoint","regular"],
  ["desencantar","to disenchant","regular"],["desiludir","to disillusion","regular"],["mortificar","to mortify","regular"],
  ["cobiçar","to covet","regular"],["repudiar","to repudiate","regular"],["desdenhar","to disdain","regular"],
  ["menosprezar","to belittle","regular"],["subestimar","to underestimate","regular"],["sobrestimar","to overestimate","regular"],
  ["engrandecer","to magnify","regular"],["bajular","to fawn over","regular"],["lisonjear","to flatter","regular"],
  ["ruborizar","to make blush","regular"],["corar","to blush","regular"],["empalidecer","to turn pale","regular"],
  ["enrubescer","to redden","regular"],["cortejar","to court","regular"],["galantear","to woo","regular"],
  ["pedalar","to pedal","regular"],["esquiar","to ski","regular"],["patinar","to skate","regular"],
  ["surfar","to surf","regular"],["flexionar","to flex","regular"],["driblar","to dribble","regular"],
  ["chutar","to kick","regular"],["rematar","to shoot at goal","regular"],["lançar","to throw","regular"],
  ["cronometrar","to time","regular"],["desqualificar","to disqualify","regular"],["qualificar","to qualify","regular"],
  ["rivalizar","to rival","regular"],["batalhar","to battle","regular"],["guerrear","to wage war","regular"],
  ["pelejar","to struggle","regular"],["contender","to contend","regular"],["confrontar","to confront","regular"],
  ["emboscar","to ambush","regular"],["desafinar","to be out of tune","regular"],["harmonizar","to harmonize","regular"],
  ["reger","to conduct","regular"],["tanger","to pluck strings","regular"],["tingir","to dye","regular"],
  ["matizar","to tint","regular"],["sombrear","to shade","regular"],["musicar","to set to music","regular"],
  ["orquestrar","to orchestrate","regular"],["versejar","to versify","regular"],["parafrasear","to paraphrase","regular"],
  ["transcrever","to transcribe","regular"],["decifrar","to decipher","regular"],["incumbir","to entrust","regular"],
  ["recrutar","to recruit","regular"],["reeleger","to reelect","regular"],["credenciar","to accredit","regular"],
  ["identificar","to identify","regular"],["justificar","to justify","regular"],["retificar","to rectify","regular"],
  ["purificar","to purify","regular"],["unificar","to unify","regular"],["modificar","to modify","regular"],
  ["amplificar","to amplify","regular"],["simplificar","to simplify","regular"],["clarificar","to clarify","regular"],
  ["especificar","to specify","regular"],["generalizar","to generalize","regular"],["particularizar","to particularize","regular"],
  ["enumerar","to enumerate","regular"],["detalhar","to detail","regular"],["sistematizar","to systematize","regular"],
  ["padronizar","to standardize","regular"],["normalizar","to normalize","regular"],["regularizar","to regularize","regular"],
  ["formalizar","to formalize","regular"],["oficializar","to make official","regular"],["responsabilizar","to hold responsible","regular"],
  ["sensibilizar","to raise awareness","regular"],["concretizar","to make concrete","regular"],["materializar","to materialize","regular"],
  ["minimizar","to minimize","regular"],["maximizar","to maximize","regular"],["rentabilizar","to make profitable","regular"],
  ["globalizar","to globalize","regular"],["industrializar","to industrialize","regular"],["urbanizar","to urbanize","regular"],
  ["centralizar","to centralize","regular"],["descentralizar","to decentralize","regular"],["democratizar","to democratize","regular"],
  ["politizar","to politicize","regular"],["caracterizar","to characterize","regular"],["dramatizar","to dramatize","regular"],
  ["protagonizar","to star in","regular"],["popularizar","to popularize","regular"],["vulgarizar","to popularize","regular"],
  ["banalizar","to trivialize","regular"],["simbolizar","to symbolize","regular"],["ironizar","to be ironic","regular"],
  ["diversificar","to diversify","regular"],["ramificar","to branch out","regular"],["petrificar","to petrify","regular"],
  ["mumificar","to mummify","regular"],["sacrificar","to sacrifice","regular"],["estratificar","to stratify","regular"],
  ["personificar","to personify","regular"],["dignificar","to dignify","regular"],["mistificar","to mystify","regular"],
  ["falsear","to falsify","regular"],["estatizar","to nationalize","regular"],["edificar","to build","regular"],
  ["erguer","to erect","regular"],["erigir","to erect","regular"],["alicerçar","to lay foundations","regular"],
  ["escorar","to shore up","regular"],["estruturar","to structure","regular"],["derrubar","to knock down","regular"],
  ["ruir","to collapse","regular"],["desabar","to cave in","regular"],["pavimentar","to pave","regular"],
  ["asfaltar","to asphalt","regular"],["calcetar","to pave with stones","regular"],["cimentar","to cement","regular"],
  ["betonar","to lay concrete","regular"],["telhar","to roof","regular"],["vedar","to seal off","regular"],
  ["murar","to wall","regular"],["demarcar","to demarcate","regular"],["balizar","to stake out","regular"],
  ["estacar","to stake","regular"],["fincar","to drive in","regular"],["cravar","to drive in","regular"],
  ["blindar","to armour","regular"],["fortificar","to fortify","regular"],["escudar","to shield","regular"],
  ["gesticular","to gesticulate","regular"],["bisbilhotar","to snoop","regular"],["coscuvilhar","to gossip","regular"],
  ["mexericar","to gossip","regular"],["tagarelar","to chatter","regular"],["palrar","to prattle","regular"],
  ["cochichar","to whisper","regular"],["segredar","to whisper a secret","regular"],["resmungar","to grumble","regular"],
  ["refilar","to grumble","regular"],["praguejar","to swear","regular"],["blasfemar","to blaspheme","regular"],
  ["denunciar","to denounce","regular"],["enunciar","to enunciate","regular"],["potenciar","to enhance","regular"],
  ["evidenciar","to make evident","regular"],["licenciar","to license","regular"],["alardear","to flaunt","regular"],
  ["gracejar","to jest","regular"],["fulgurar","to flash","regular"],["faiscar","to spark","regular"],
  ["tremeluzir","to flicker","regular"],["bruxulear","to flicker","regular"],["piscar","to blink","regular"],
  ["pestanejar","to blink","regular"],["abrasar","to scorch","regular"],["chamuscar","to singe","regular"],
  ["crestar","to scorch","regular"],["carbonizar","to char","regular"],["calcinar","to calcine","regular"],
  ["flamejar","to blaze","regular"],["deflagrar","to break out","regular"],["detonar","to detonate","regular"],
  ["dinamitar","to dynamite","regular"],["bombardear","to bombard","regular"],["minar","to undermine","regular"],
  ["rebentar","to burst","regular"],["estourar","to burst","regular"],["reverberar","to reverberate","regular"],
  ["embeber","to soak up","regular"],["entornar","to spill","regular"],["derramar","to spill","regular"],
  ["esguichar","to squirt","regular"],["boiar","to float","regular"],["ressumar","to ooze","regular"],
  ["zunir","to whir","regular"],["silvar","to hiss","regular"],["sibilar","to hiss","regular"],
  ["chocalhar","to rattle","regular"],["tilintar","to tinkle","regular"],["retinir","to clang","regular"],
  ["tinir","to clink","regular"],["repicar","to chime","regular"],["badalar","to toll","regular"],
  ["ribombar","to boom","regular"],["zurrar","to bray","regular"],["grunhir","to grunt","regular"],
  ["roncar","to snore","regular"],["rosnar","to growl","regular"],["piar","to chirp","regular"],
  ["pipilar","to cheep","regular"],["cacarejar","to cackle","regular"],["cocoricar","to crow","regular"],
  ["gralhar","to squawk","regular"],["urrar","to roar","regular"],["ganir","to yelp","regular"],
  ["enxaguar","to rinse","regular"],["ensaboar","to soap up","regular"],["higienizar","to sanitize","regular"],
  ["depilar","to wax","regular"],["perfumar","to perfume","regular"],["empoar","to powder","regular"],
  ["saquear","to loot","regular"],["pilhar","to pillage","regular"],["depredar","to vandalize","regular"],
  ["devastar","to devastate","regular"],["exterminar","to exterminate","regular"],["dizimar","to decimate","regular"],
  ["chacinar","to massacre","regular"],["massacrar","to massacre","regular"],["trucidar","to slaughter","regular"],
  ["fuzilar","to execute by firing squad","regular"],["enforcar","to hang","regular"],["decapitar","to behead","regular"],
  ["degolar","to slit the throat of","regular"],["esfaquear","to stab","regular"],["disparar","to fire","regular"],
  ["recarregar","to reload","regular"],["engatilhar","to cock a gun","regular"],["sitiar","to besiege","regular"],
  ["resgatar","to rescue","regular"],["socorrer","to come to the aid of","regular"],["salvaguardar","to safeguard","regular"],
  ["resguardar","to shelter","regular"],["aprisionar","to imprison","regular"],["encarcerar","to incarcerate","regular"],
  ["enclausurar","to cloister","regular"],["encurralar","to corner","regular"],["subjugar","to subjugate","regular"],
  ["escravizar","to enslave","regular"],["emancipar","to emancipate","regular"],["tiranizar","to tyrannize","regular"],
  ["comercializar","to trade","regular"],["mercadejar","to peddle","regular"],["traficar","to traffic","regular"],
  ["exportar","to export","regular"],["importar","to import","regular"],["aprovisionar","to provision","regular"],
  ["acrescer","to accrue","regular"],["decrescer","to decrease","regular"],["minguar","to wane","regular"],
  ["avolumar","to bulk up","regular"],["adensar","to thicken","regular"],["dilatar","to dilate","regular"],
  ["inflar","to inflate","regular"],["intumescer","to swell","regular"],["sufragar","to vote for","regular"],
  ["destronar","to dethrone","regular"],["entronizar","to enthrone","regular"],["coroar","to crown","regular"],
  ["rezar","to pray","regular"],["orar","to pray","regular"],["abençoar","to bless","regular"],
  ["excomungar","to excommunicate","regular"],["consagrar","to consecrate","regular"],["profanar","to desecrate","regular"],
  ["santificar","to sanctify","regular"],["pecar","to sin","regular"],["redimir","to redeem","regular"],
  ["expiar","to atone for","regular"],["peregrinar","to make a pilgrimage","regular"],["evangelizar","to evangelize","regular"],
  ["catequizar","to catechize","regular"],["crucificar","to crucify","regular"],["crismar","to confirm in faith","regular"],
  ["conjeturar","to conjecture","regular"],["especular","to speculate","regular"],["teorizar","to theorize","regular"],
  ["postular","to postulate","regular"],["rebater","to refute","regular"],["objetar","to object","regular"],
  ["arguir","to argue","regular"],["englobar","to encompass","regular"],["circunscrever","to circumscribe","regular"],
  ["correlacionar","to correlate","regular"],["dissociar","to dissociate","regular"],["conotar","to connote","regular"],
  ["denotar","to denote","regular"],["significar","to signify","regular"],["conceituar","to conceptualize","regular"],
  ["redefinir","to redefine","regular"],["esquematizar","to outline","regular"],["equiparar","to equate","regular"],
  ["corresponder","to correspond","regular"],["subordinar","to subordinate","regular"],["sujeitar","to subject","regular"],
  ["submeter","to submit","regular"],["remeter","to remit","regular"],["cometer","to commit","regular"],
  ["omitir","to omit","regular"],["remitir","to remit","regular"],["interceder","to intercede","regular"],
  ["depreender","to infer","regular"],["desvendar","to unveil","regular"],["desvelar","to reveal","regular"],
  ["obscurecer","to obscure","regular"],["complicar","to complicate","regular"],["estabilizar","to stabilize","regular"],
  ["desestabilizar","to destabilize","regular"],["equilibrar","to balance","regular"],["desequilibrar","to unbalance","regular"],
  ["desarmar","to disarm","regular"],["desorganizar","to disorganize","regular"],["desordenar","to throw into disorder","regular"],
  ["desarrumar","to mess up","regular"],["desalinhar","to misalign","regular"],["habilitar","to enable","regular"],
  ["desabilitar","to disable","regular"],["desabafar","to vent","regular"],["desafiar","to challenge","regular"],
  ["desviar","to divert","regular"],["desencaminhar","to lead astray","regular"],["desaparecer","to disappear","regular"],
  ["desvanecer","to fade","regular"],["dissipar","to dissipate","regular"],["esfumar","to blur","regular"],
  ["desbotar","to fade in colour","regular"],["desatar","to untie","regular"],["desamarrar","to unmoor","regular"],
  ["desprender","to detach","regular"],["liberar","to release","regular"],["desencadear","to trigger","regular"],
  ["encadear","to chain together","regular"],["enroscar","to coil","regular"],["enrolar","to roll up","regular"],
  ["desenrolar","to unroll","regular"],["desenrascar","to sort out a fix","regular"],["emaranhar","to tangle","regular"],
  ["desemaranhar","to disentangle","regular"],["enredar","to ensnare","regular"],["embaraçar","to embarrass","regular"],
  ["desembaraçar","to disentangle","regular"],["enfaixar","to bandage","regular"],["ensacar","to bag","regular"],
  ["encaixotar","to crate","regular"],["agrilhoar","to shackle","regular"],["enfeitar","to decorate","regular"],
  ["ornamentar","to ornament","regular"],["ornar","to adorn","regular"],["embelezar","to embellish","regular"],
  ["enfeiar","to make ugly","regular"],["afear","to disfigure","regular"],["desfigurar","to disfigure","regular"],
  ["enlouquecer","to drive mad","regular"],["endoidecer","to go crazy","regular"],["entontecer","to make dizzy","regular"],
  ["desnortear","to disorient","regular"],["desorientar","to disorient","regular"],["nortear","to guide","regular"],
  ["robustecer","to make robust","regular"],["enrijecer","to stiffen","regular"],["enrijar","to harden","regular"],
  ["envenenar","to poison","regular"],["intoxicar","to intoxicate","regular"],["amargurar","to embitter","regular"],
  ["esticar","to stretch","regular"],["encurtar","to shorten","regular"],["estilhaçar","to shatter","regular"],
  ["despedaçar","to tear to pieces","regular"],["esmigalhar","to crumble","regular"],["danificar","to damage","regular"],
  ["avariar","to break down","regular"],["deteriorar","to deteriorate","regular"],["desgastar","to wear out","regular"],
  ["rasgar","to tear","regular"],["lacerar","to lacerate","regular"],["dilacerar","to tear apart","regular"],
  ["mutilar","to mutilate","regular"],["decepar","to sever","regular"],["cercear","to curtail","regular"],
  ["truncar","to truncate","regular"],["desbastar","to trim","regular"],["fender","to split","regular"],
  ["romper","to break","regular"],["corromper","to corrupt","regular"],["amalgamar","to amalgamate","regular"],
  ["mesclar","to blend","regular"],["entrelaçar","to interlace","regular"],["interligar","to interconnect","regular"],
  ["enlaçar","to link","regular"],["aglutinar","to agglutinate","regular"],["aglomerar","to cluster","regular"],
  ["congregar","to congregate","regular"],["coligar","to ally","regular"],["parear","to pair","regular"],
  ["conjugar","to combine","regular"],["compatibilizar","to make compatible","regular"],["desunir","to disunite","regular"],
  ["desmembrar","to dismember","regular"],["desagregar","to break up","regular"],["desmantelar","to dismantle","regular"],
  ["distanciar","to distance","regular"],["alhear","to alienate","regular"],["alienar","to alienate","regular"],
  ["caprichar","to do with flair","regular"],["vadiar","to loaf about","regular"],["mandriar","to laze","regular"],
  ["preguiçar","to be idle","regular"],["empenhar","to pledge","regular"],["aquiescer","to acquiesce","regular"],
  ["infligir","to inflict","regular"],["infringir","to infringe","regular"],["constranger","to constrain","regular"],
  ["ungir","to anoint","regular"],["urgir","to be urgent","regular"],["ressurgir","to resurge","regular"],
  ["imiscuir","to interfere","regular"],["albergar","to shelter","regular"],["aderir","to adhere","stem-change"],
  ["advertir","to warn","stem-change"],["agredir","to assault","stem-change"],["transgredir","to transgress","stem-change"],
  ["progredir","to progress","stem-change"],["regredir","to regress","stem-change"],["denegrir","to denigrate","stem-change"],
  ["divergir","to diverge","stem-change"],["convergir","to converge","stem-change"],["aspergir","to sprinkle","stem-change"],
  ["submergir","to submerge","stem-change"],["emergir","to emerge","stem-change"],["imergir","to immerse","stem-change"],
  ["expelir","to expel","stem-change"],["compelir","to compel","stem-change"],["repelir","to repel","stem-change"],
  ["impelir","to impel","stem-change"],["deferir","to grant","stem-change"],["indeferir","to reject a request","stem-change"],
  ["diferir","to differ","stem-change"],["proferir","to utter","stem-change"],["aferir","to gauge","stem-change"],
  ["conferir","to check","stem-change"],["interferir","to interfere","stem-change"],["ingerir","to ingest","stem-change"],
  ["inserir","to insert","stem-change"],["reinserir","to reinsert","stem-change"],["expedir","to dispatch","stem-change"],
  ["gemir","to groan","stem-change"],["revestir","to coat","stem-change"],["travestir","to disguise","stem-change"],
  ["perseguir","to pursue","stem-change"],["pressentir","to sense beforehand","stem-change"],["consentir","to consent","stem-change"],
  ["assentir","to assent","stem-change"],["ressentir","to resent","stem-change"],["desmentir","to deny","stem-change"],
  ["encobrir","to conceal","stem-change"],["recobrir","to cover over","stem-change"],["sumir","to vanish","stem-change"],
  ["consumir","to consume","stem-change"],["cuspir","to spit","stem-change"],["acudir","to come to the aid of","stem-change"],
  ["sacudir","to shake off","stem-change"],["bulir","to fidget","stem-change"],["haver","to have (auxiliary)","irregular"],
  ["reaver","to recover","irregular"],["caber","to fit","irregular"],["crer","to believe","irregular"],
  ["descrer","to disbelieve","irregular"],["equivaler","to be equivalent","irregular"],["requerer","to request","irregular"],
  ["obter","to obtain","irregular"],["reter","to retain","irregular"],["manter","to maintain","irregular"],
  ["suster","to hold up","irregular"],["abster-se","to abstain","irregular"],["ater-se","to abide by","irregular"],
  ["convir","to be suitable","irregular"],["intervir","to intervene","irregular"],["desfazer","to undo","irregular"],
  ["refazer","to redo","irregular"],["perfazer","to make up a total","irregular"],["liquefazer","to liquefy","irregular"],
  ["desdizer","to retract","irregular"],["bendizer","to bless","irregular"],["maldizer","to curse","irregular"],
  ["introduzir","to introduce","irregular"],["seduzir","to seduce","irregular"],["aduzir","to adduce","irregular"],
  ["reconduzir","to lead back","irregular"],["atrair","to attract","irregular"],["extrair","to extract","irregular"],
  ["retrair","to retract","irregular"],["abstrair","to abstract","irregular"],["recair","to relapse","irregular"],
  ["decair","to decline","irregular"],["esvair-se","to fade away","irregular"],["descontrair","to relax","irregular"],
  ["esforçar-se","to strive","regular"],["suicidar-se","to commit suicide","regular"],["vangloriar-se","to boast","regular"],
  ["regozijar-se","to rejoice","regular"],["apiedar-se","to take pity","regular"],["compadecer-se","to sympathize","regular"],
  ["esmerar-se","to take great care","regular"],["precaver-se","to take precautions","regular"],["intrometer-se","to meddle","regular"],
  ["atarefar-se","to busy oneself","regular"],["apaixonar-se","to fall in love","regular"],["enamorar-se","to fall in love","regular"]
];

// Lookup table for irregular conjugations (only the forms that deviate from the
// regular rules). Stored as { tense: [eu, tu, ele, nós, vós, eles] }.
// The engine derives conditional from `future`; and pluperfect, imperfect
// subjunctive and future subjunctive from the preterite — so an irregular
// preterite automatically feeds all three. `present_subj` is stored ONLY when it
// is suppletive (not derivable from the eu present indicative).
const IRREGULAR_FORMS = {
  ser: {
    present: ["sou","és","é","somos","sois","são"],
    preterite: ["fui","foste","foi","fomos","fostes","foram"],
    imperfect: ["era","eras","era","éramos","éreis","eram"],
    present_subj: ["seja","sejas","seja","sejamos","sejais","sejam"]
  },
  estar: {
    present: ["estou","estás","está","estamos","estais","estão"],
    preterite: ["estive","estiveste","esteve","estivemos","estivestes","estiveram"],
    present_subj: ["esteja","estejas","esteja","estejamos","estejais","estejam"]
  },
  ter: {
    present: ["tenho","tens","tem","temos","tendes","têm"],
    preterite: ["tive","tiveste","teve","tivemos","tivestes","tiveram"],
    imperfect: ["tinha","tinhas","tinha","tínhamos","tínheis","tinham"]
  },
  haver: {
    present: ["hei","hás","há","havemos","haveis","hão"],
    preterite: ["houve","houveste","houve","houvemos","houvestes","houveram"],
    present_subj: ["haja","hajas","haja","hajamos","hajais","hajam"]
  },
  fazer: {
    present: ["faço","fazes","faz","fazemos","fazeis","fazem"],
    preterite: ["fiz","fizeste","fez","fizemos","fizestes","fizeram"],
    future: ["farei","farás","fará","faremos","fareis","farão"]
  },
  poder: {
    present: ["posso","podes","pode","podemos","podeis","podem"],
    preterite: ["pude","pudeste","pôde","pudemos","pudestes","puderam"]
  },
  dizer: {
    present: ["digo","dizes","diz","dizemos","dizeis","dizem"],
    preterite: ["disse","disseste","disse","dissemos","dissestes","disseram"],
    future: ["direi","dirás","dirá","diremos","direis","dirão"]
  },
  ir: {
    present: ["vou","vais","vai","vamos","ides","vão"],
    preterite: ["fui","foste","foi","fomos","fostes","foram"],
    present_subj: ["vá","vás","vá","vamos","vades","vão"]
  },
  ver: {
    present: ["vejo","vês","vê","vemos","vedes","veem"],
    preterite: ["vi","viste","viu","vimos","vistes","viram"]
  },
  vir: {
    present: ["venho","vens","vem","vimos","vindes","vêm"],
    preterite: ["vim","vieste","veio","viemos","viestes","vieram"],
    imperfect: ["vinha","vinhas","vinha","vínhamos","vínheis","vinham"]
  },
  dar: {
    present: ["dou","dás","dá","damos","dais","dão"],
    preterite: ["dei","deste","deu","demos","destes","deram"],
    present_subj: ["dê","dês","dê","demos","deis","deem"]
  },
  saber: {
    present: ["sei","sabes","sabe","sabemos","sabeis","sabem"],
    preterite: ["soube","soubeste","soube","soubemos","soubestes","souberam"],
    present_subj: ["saiba","saibas","saiba","saibamos","saibais","saibam"]
  },
  querer: {
    present: ["quero","queres","quer","queremos","quereis","querem"],
    preterite: ["quis","quiseste","quis","quisemos","quisestes","quiseram"],
    present_subj: ["queira","queiras","queira","queiramos","queirais","queiram"]
  },
  trazer: {
    present: ["trago","trazes","traz","trazemos","trazeis","trazem"],
    preterite: ["trouxe","trouxeste","trouxe","trouxemos","trouxestes","trouxeram"],
    future: ["trarei","trarás","trará","traremos","trareis","trarão"]
  },
  ler: {
    present: ["leio","lês","lê","lemos","ledes","leem"],
    preterite: ["li","leste","leu","lemos","lestes","leram"]
  },
  crer: {
    present: ["creio","crês","crê","cremos","credes","creem"],
    preterite: ["cri","creste","creu","cremos","crestes","creram"]
  },
  "pôr": {
    present: ["ponho","pões","põe","pomos","pondes","põem"],
    preterite: ["pus","puseste","pôs","pusemos","pusestes","puseram"],
    imperfect: ["punha","punhas","punha","púnhamos","púnheis","punham"],
    future: ["porei","porás","porá","poremos","poreis","porão"]
  },
  ouvir: {
    present: ["ouço","ouves","ouve","ouvimos","ouvis","ouvem"]
  },
  pedir: {
    present: ["peço","pedes","pede","pedimos","pedis","pedem"]
  },
  medir: {
    present: ["meço","medes","mede","medimos","medis","medem"]
  },
  perder: {
    present: ["perco","perdes","perde","perdemos","perdeis","perdem"]
  },
  sair: {
    present: ["saio","sais","sai","saímos","saís","saem"],
    preterite: ["saí","saíste","saiu","saímos","saístes","saíram"]
  },
  cair: {
    present: ["caio","cais","cai","caímos","caís","caem"],
    preterite: ["caí","caíste","caiu","caímos","caístes","caíram"]
  },
  rir: {
    present: ["rio","ris","ri","rimos","rides","riem"],
    preterite: ["ri","riste","riu","rimos","ristes","riram"]
  },
  sorrir: {
    present: ["sorrio","sorris","sorri","sorrimos","sorrides","sorriem"]
  },
  dormir: {
    present: ["durmo","dormes","dorme","dormimos","dormis","dormem"]
  },
  subir: {
    present: ["subo","sobes","sobe","subimos","subis","sobem"]
  },
  fugir: {
    present: ["fujo","foges","foge","fugimos","fugis","fogem"]
  },
  servir: {
    present: ["sirvo","serves","serve","servimos","servis","servem"]
  },
  seguir: {
    present: ["sigo","segues","segue","seguimos","seguis","seguem"]
  },
  conseguir: {
    present: ["consigo","consegues","consegue","conseguimos","conseguis","conseguem"]
  },
  perseguir: {
    present: ["persigo","persegues","persegue","perseguimos","perseguis","perseguem"]
  },
  prosseguir: {
    present: ["prossigo","prossegues","prossegue","prosseguimos","prosseguis","prosseguem"]
  },
  sentir: {
    present: ["sinto","sentes","sente","sentimos","sentis","sentem"]
  },
  mentir: {
    present: ["minto","mentes","mente","mentimos","mentis","mentem"]
  },
  preferir: {
    present: ["prefiro","preferes","prefere","preferimos","preferis","preferem"]
  },
  repetir: {
    present: ["repito","repetes","repete","repetimos","repetis","repetem"]
  },
  vestir: {
    present: ["visto","vestes","veste","vestimos","vestis","vestem"]
  },
  conhecer: {
    present: ["conheço","conheces","conhece","conhecemos","conheceis","conhecem"]
  },
  parecer: {
    present: ["pareço","pareces","parece","parecemos","pareceis","parecem"]
  },
  esquecer: {
    present: ["esqueço","esqueces","esquece","esquecemos","esqueceis","esquecem"]
  },
  oferecer: {
    present: ["ofereço","ofereces","oferece","oferecemos","ofereceis","oferecem"]
  },
  nascer: {
    present: ["nasço","nasces","nasce","nascemos","nasceis","nascem"]
  },
  descer: {
    present: ["desço","desces","desce","descemos","desceis","descem"]
  },
  crescer: {
    present: ["cresço","cresces","cresce","crescemos","cresceis","crescem"]
  },
  dirigir: {
    present: ["dirijo","diriges","dirige","dirigimos","dirigis","dirigem"]
  },
  proteger: {
    present: ["protejo","proteges","protege","protegemos","protegeis","protegem"]
  },
  conduzir: {
    present: ["conduzo","conduzes","conduz","conduzimos","conduzis","conduzem"]
  },
  produzir: {
    present: ["produzo","produzes","produz","produzimos","produzis","produzem"]
  },
  traduzir: {
    present: ["traduzo","traduzes","traduz","traduzimos","traduzis","traduzem"]
  }
};

// Compound verbs inherit their base verb's irregular forms by prefixing, so we
// don't have to list manter, compor, rever, convir, desfazer, … by hand.
const COMPOUND_BASE = {
  compor:"pôr", dispor:"pôr", propor:"pôr", supor:"pôr", impor:"pôr", expor:"pôr",
  repor:"pôr", depor:"pôr", opor:"pôr", sobrepor:"pôr", pressupor:"pôr", transpor:"pôr",
  antepor:"pôr", interpor:"pôr", justapor:"pôr", decompor:"pôr", recompor:"pôr",
  predispor:"pôr", contrapor:"pôr", indispor:"pôr",
  manter:"ter", obter:"ter", conter:"ter", deter:"ter", reter:"ter", entreter:"ter",
  suster:"ter", abster:"ter",
  rever:"ver", prever:"ver", antever:"ver", entrever:"ver",
  convir:"vir", intervir:"vir", provir:"vir", advir:"vir", sobrevir:"vir", desavir:"vir",
  desfazer:"fazer", refazer:"fazer", satisfazer:"fazer", contrafazer:"fazer", perfazer:"fazer",
  contradizer:"dizer", predizer:"dizer", desdizer:"dizer", bendizer:"dizer", maldizer:"dizer",
};
const COMPOUND_TAIL = { "pôr": /por$/, ter: /ter$/, ver: /ver$/, vir: /vir$/, fazer: /fazer$/, dizer: /dizer$/ };

function buildCompound(inf) {
  let base = COMPOUND_BASE[inf];
  // Any verb ending in -por is a pôr-compound (apor, pospor, sobrepor, …).
  if (!base && inf.endsWith("por") && inf.length > 3) base = "pôr";
  if (!base) return null;
  const baseForms = IRREGULAR_FORMS[base];
  const prefix = inf.replace(COMPOUND_TAIL[base], "");
  const out = {};
  for (const t of Object.keys(baseForms)) out[t] = baseForms[t].map((f) => prefix + f);
  // ter / vir compounds: the 2sg & 3sg present take an acute (manténs, mantém;
  // convéns, convém) once the form is no longer a stressed monosyllable.
  if ((base === "ter" || base === "vir") && out.present) {
    out.present[1] = out.present[1].replace(/e(ns|m)$/, "é$1");
    out.present[2] = out.present[2].replace(/e(ns|m)$/, "é$1");
  }
  return out;
}

function buildSuffixIrregular(inf) {
  // -air verbs follow the cair/sair pattern (trair, atrair, extrair, recair…).
  if (inf.endsWith("air") && inf.length > 3) {
    const p = inf.slice(0, -3);
    return {
      present:   [p + "aio", p + "ais", p + "ai", p + "aímos", p + "aís", p + "aem"],
      preterite: [p + "aí", p + "aíste", p + "aiu", p + "aímos", p + "aístes", p + "aíram"],
    };
  }
  // -uir verbs with a vowel u (construir, incluir, possuir…), not -guir/-quir.
  if (/[^gq]uir$/.test(inf) && inf.length > 4) {
    const p = inf.slice(0, -3);
    return {
      present:   [p + "uo", p + "uis", p + "ui", p + "uímos", p + "uís", p + "uem"],
      preterite: [p + "uí", p + "uíste", p + "uiu", p + "uímos", p + "uístes", p + "uíram"],
    };
  }
  // -zir verbs: the 3sg drops the theme vowel (reduz, conduz, traduz); eu regular.
  if (inf.endsWith("zir") && inf.length > 4) {
    const st = inf.slice(0, -2);
    return { present: [st + "o", st + "es", st, st + "imos", st + "is", st + "em"] };
  }
  // -cer verbs: the eu softens c→ç (venço, aqueço, torço). cocer is the exception.
  if (inf.endsWith("cer") && inf.length > 4) {
    if (inf.endsWith("cocer")) {
      const st = inf.slice(0, -5);
      return { present: [st + "cozo", st + "cozes", st + "coze", st + "cozemos", st + "cozeis", st + "cozem"] };
    }
    const st = inf.slice(0, -3);
    return { present: [st + "ço", st + "ces", st + "ce", st + "cemos", st + "ceis", st + "cem"] };
  }
  // -ger verbs: the eu shifts g→j (protejo, abranjo).
  if (inf.endsWith("ger") && inf.length > 4) {
    const st = inf.slice(0, -3);
    return { present: [st + "jo", st + "ges", st + "ge", st + "gemos", st + "geis", st + "gem"] };
  }
  // -gir verbs: the eu shifts g→j (finjo, exijo, dirijo).
  if (inf.endsWith("gir") && inf.length > 4) {
    const st = inf.slice(0, -3);
    return { present: [st + "jo", st + "ges", st + "ge", st + "gimos", st + "gis", st + "gem"] };
  }
  // -guir verbs (non-raisers): the eu drops the u (distingo, extingo).
  if (inf.endsWith("guir") && inf.length > 5) {
    const st = inf.slice(0, -4);
    return { present: [st + "go", st + "gues", st + "gue", st + "guimos", st + "guis", st + "guem"] };
  }
  return null;
}

// Resolved irregular forms: explicit table, else a synthesised compound/suffix set.
const _irrCache = new Map();
function irrForms(inf) {
  if (_irrCache.has(inf)) return _irrCache.get(inf);
  const r = IRREGULAR_FORMS[inf] || buildCompound(inf) || buildSuffixIrregular(inf) || null;
  _irrCache.set(inf, r);
  return r;
}

// Small legacy vocabulary array (shape { pt, en, cat }) used by some older views.
const VOCABULARY = [
  { pt: "a casa", en: "house", cat: "noun" },{ pt: "o cão", en: "dog", cat: "noun" },
  { pt: "o gato", en: "cat", cat: "noun" },{ pt: "a água", en: "water", cat: "noun" },
  { pt: "a comida", en: "food", cat: "noun" },{ pt: "o livro", en: "book", cat: "noun" },
  { pt: "o amigo", en: "friend", cat: "noun" },{ pt: "a família", en: "family", cat: "noun" },
  { pt: "o tempo", en: "time / weather", cat: "noun" },{ pt: "o trabalho", en: "work / job", cat: "noun" },
  { pt: "a cidade", en: "city", cat: "noun" },{ pt: "o país", en: "country", cat: "noun" },
  { pt: "o mundo", en: "world", cat: "noun" },{ pt: "a vida", en: "life", cat: "noun" },
  { pt: "o dia", en: "day", cat: "noun" },{ pt: "a noite", en: "night", cat: "noun" },
  { pt: "o ano", en: "year", cat: "noun" },{ pt: "o mês", en: "month", cat: "noun" },
  { pt: "a semana", en: "week", cat: "noun" },{ pt: "a hora", en: "hour", cat: "noun" },
  { pt: "grande", en: "big", cat: "adj" },{ pt: "pequeno", en: "small", cat: "adj" },
  { pt: "bom", en: "good", cat: "adj" },{ pt: "mau", en: "bad", cat: "adj" },
  { pt: "novo", en: "new", cat: "adj" },{ pt: "velho", en: "old", cat: "adj" },
  { pt: "feliz", en: "happy", cat: "adj" },{ pt: "triste", en: "sad", cat: "adj" },
  { pt: "bonito", en: "pretty", cat: "adj" },{ pt: "feio", en: "ugly", cat: "adj" },
  { pt: "rápido", en: "fast", cat: "adj" },{ pt: "lento", en: "slow", cat: "adj" },
  { pt: "alto", en: "tall / high", cat: "adj" },{ pt: "baixo", en: "short / low", cat: "adj" },
  { pt: "caro", en: "expensive", cat: "adj" },{ pt: "barato", en: "cheap", cat: "adj" },
  { pt: "vermelho", en: "red", cat: "color" },{ pt: "azul", en: "blue", cat: "color" },
  { pt: "verde", en: "green", cat: "color" },{ pt: "amarelo", en: "yellow", cat: "color" },
  { pt: "preto", en: "black", cat: "color" },{ pt: "branco", en: "white", cat: "color" },
  { pt: "olá", en: "hello", cat: "phrase" },{ pt: "adeus", en: "goodbye", cat: "phrase" },
  { pt: "por favor", en: "please", cat: "phrase" },{ pt: "obrigado", en: "thank you", cat: "phrase" },
  { pt: "de nada", en: "you're welcome", cat: "phrase" },{ pt: "desculpa", en: "I'm sorry", cat: "phrase" },
  { pt: "bom dia", en: "good morning", cat: "phrase" },{ pt: "boa noite", en: "good night", cat: "phrase" }
];

// Visual dual-coding for concrete, picturable verbs, keyed by Portuguese
// infinitive. Abstract verbs are intentionally left blank.
const VERB_EMOJI = {
  abraçar: "🤗", abrir: "🔓", apanhar: "🥅", dançar: "💃", beber: "🥤",
  beijar: "😘", caminhar: "🚶", cantar: "🎤", caçar: "🏹", fechar: "🔒",
  cozinhar: "🍳", comer: "🍽️", comprar: "🛒", conduzir: "🚗", construir: "🏗️",
  correr: "🏃", cortar: "✂️", descansar: "😌", acordar: "⏰", desenhar: "✏️",
  dormir: "😴", empurrar: "👐", enviar: "📨", esconder: "🙈", escutar: "👂",
  estudar: "📚", assinar: "🖊️", fumar: "🚬", ganhar: "🏆", gritar: "📢",
  jogar: "🎮", lançar: "🤾", lavar: "🧼", ler: "📖", limpar: "🧹",
  chorar: "😢", chover: "🌧️", montar: "🐎", nadar: "🏊",
  nevar: "❄️", ouvir: "👂", pagar: "💳", pescar: "🎣",
  pintar: "🎨", plantar: "🌱", regar: "💧", respirar: "🫁", rir: "😄",
  partir: "💥", saltar: "🤸", sorrir: "🙂", puxar: "🪢", tocar: "🎸",
  trazer: "🎁", vender: "🏷️", viajar: "✈️", voar: "🛫",
};

// Build the complete verb list (top 50 + additional, deduplicated).
function buildVerbList() {
  const seen = new Set();
  const verbs = [];
  TOP_50_VERBS.forEach((v, i) => {
    seen.add(v.inf);
    verbs.push({ ...v, emoji: v.emoji || VERB_EMOJI[v.inf] || "", rank: i + 1, top: true });
  });
  ADDITIONAL_VERBS.forEach(([inf, en, group], i) => {
    if (seen.has(inf)) return;
    seen.add(inf);
    verbs.push({ inf, en, group, rank: 50 + i + 1, top: false, emoji: VERB_EMOJI[inf] || "", note: "" });
  });
  return verbs;
}

const ALL_VERBS = buildVerbList();

// ============================================================
// CONJUGATION ENGINE
// ============================================================
const PRONOUNS = ["eu", "tu", "ele/ela", "nós", "vós", "eles/elas"];
const TENSES = ["present", "preterite", "imperfect", "pluperfect", "future", "conditional"];
const TENSE_LABELS = {
  present: "Presente",
  preterite: "Pretérito Perfeito",
  imperfect: "Pretérito Imperfeito",
  pluperfect: "Pretérito Mais-que-Perfeito",
  future: "Futuro",
  conditional: "Condicional",
};

function stripSe(infinitive) {
  if (infinitive.endsWith("-se")) return infinitive.slice(0, -3);
  return infinitive;
}

function getStemAndEnding(infinitive) {
  const inf = stripSe(infinitive);
  if (inf === "pôr" || inf.endsWith("pôr") || inf.endsWith("por")) {
    // pôr family conjugates like an -er verb on the "po"/"compo" stem.
    return { stem: inf.replace(/p[oô]r$/, "p"), ending: "er", por: true };
  }
  const last2 = inf.slice(-2);
  if (last2 === "ar" || last2 === "er" || last2 === "ir") {
    return { stem: inf.slice(0, -2), ending: last2 };
  }
  return { stem: inf, ending: "ar" };
}

const REGULAR_ENDINGS = {
  present: {
    ar: ["o", "as", "a", "amos", "ais", "am"],
    er: ["o", "es", "e", "emos", "eis", "em"],
    ir: ["o", "es", "e", "imos", "is", "em"],
  },
  preterite: {
    ar: ["ei", "aste", "ou", "ámos", "astes", "aram"],
    er: ["i", "este", "eu", "emos", "estes", "eram"],
    ir: ["i", "iste", "iu", "imos", "istes", "iram"],
  },
  imperfect: {
    ar: ["ava", "avas", "ava", "ávamos", "áveis", "avam"],
    er: ["ia", "ias", "ia", "íamos", "íeis", "iam"],
    ir: ["ia", "ias", "ia", "íamos", "íeis", "iam"],
  },
  future: {
    ar: ["ei", "ás", "á", "emos", "eis", "ão"],
    er: ["ei", "ás", "á", "emos", "eis", "ão"],
    ir: ["ei", "ás", "á", "emos", "eis", "ão"],
  },
  conditional: {
    ar: ["ia", "ias", "ia", "íamos", "íeis", "iam"],
    er: ["ia", "ias", "ia", "íamos", "íeis", "iam"],
    ir: ["ia", "ias", "ia", "íamos", "íeis", "iam"],
  },
};

// Acute-accent the LAST vowel of a stem — used for the 1pl/2pl of the pluperfect
// and the imperfect subjunctive (faláramos, falássemos).
function accentLastVowel(s) {
  const map = { a: "á", e: "é", i: "í", o: "ó", u: "ú" };
  for (let k = s.length - 1; k >= 0; k--) {
    if (map[s[k]]) return s.slice(0, k) + map[s[k]] + s.slice(k + 1);
  }
  return s;
}
// Circumflex variant (closed vowels): comêssemos, fôssemos.
function circumflexLastVowel(s) {
  const map = { a: "â", e: "ê", i: "í", o: "ô", u: "ú" };
  for (let k = s.length - 1; k >= 0; k--) {
    if (map[s[k]]) return s.slice(0, k) + map[s[k]] + s.slice(k + 1);
  }
  return s;
}
// Accent by conjugation class: -ar/-ir take an acute, -er a circumflex.
function accentByClass(stem, ending) {
  return ending === "er" ? circumflexLastVowel(stem) : accentLastVowel(stem);
}
// The 1pl/2pl accent for the pluperfect & imperfect subjunctive. Regular verbs
// follow their class (falá-, comê-, partí-); strong (irregular-preterite) verbs
// take an acute on the stem vowel (fizé-, tivé-, pusé-), except ser/ir → fô-.
function accentDerivedForm(infinitive, stem) {
  const inf = stripSe(infinitive);
  const irr = irrForms(inf);
  if (!(irr && irr.preterite)) {
    const { ending } = getStemAndEnding(infinitive);
    return accentByClass(stem, ending);
  }
  if (inf === "ser" || inf === "ir") return circumflexLastVowel(stem);
  return accentLastVowel(stem);
}

function regularConjugate(infinitive, tense) {
  const { stem, ending } = getStemAndEnding(infinitive);
  const inf = stripSe(infinitive);
  if (tense === "future" || tense === "conditional") {
    return REGULAR_ENDINGS[tense][ending].map((s) => inf + s);
  }
  if (tense === "pluperfect") {
    const stemP = preteriteThirdPluralStem(infinitive);
    const endings = ["ra", "ras", "ra", "ramos", "reis", "ram"];
    return endings.map((e, i) => (i === 3 || i === 4 ? accentDerivedForm(infinitive, stemP) + e : stemP + e));
  }
  const forms = REGULAR_ENDINGS[tense][ending].map((s) => stem + s);
  // -car/-gar/-çar keep their hard consonant before the preterite eu ending "-ei"
  // (ficar→fiquei, chegar→cheguei, começar→comecei, pagar→paguei).
  if (tense === "preterite" && ending === "ar") forms[0] = fixOrthographic(stem) + "ei";
  return forms;
}

// The eles preterite minus "ram" — shared base of pluperfect & imperfect subj.
function preteriteThirdPluralStem(infinitive) {
  const eles = conjugate(infinitive, "preterite").forms[5];
  return eles.endsWith("ram") ? eles.slice(0, -3) : eles;
}

function conjugate(infinitive, tense) {
  const inf = stripSe(infinitive);
  if (tense === "personal_inf") return personalInfinitive(infinitive);
  const irregular = irrForms(inf);
  const regular = regularConjugate(infinitive, tense);
  if (irregular && irregular[tense]) {
    return { forms: irregular[tense], regular };
  }
  // Conditional shares the future's (possibly irregular) stem.
  if (tense === "conditional" && irregular && irregular.future) {
    const stem = irregular.future[0].slice(0, -2); // farei → far
    const endings = ["ia", "ias", "ia", "íamos", "íeis", "iam"];
    return { forms: endings.map((e) => stem + e), regular };
  }
  return { forms: regular, regular };
}

// Compare actual vs regular per person, to flag irregular cells.
function getIrregularFlags(infinitive, tense) {
  const { forms, regular } = conjugate(infinitive, tense);
  return forms.map((f, i) => f !== regular[i]);
}

// ============================================================
// SUBJUNCTIVE / CONJUNTIVO ENGINE
// ============================================================
// Presente do conjuntivo: from the eu present indicative (drop -o) + "opposite"
//   endings (-ar → -e…, -er/-ir → -a…). The eu stem carries every present-tense
//   irregularity straight into the conjuntivo (faço→faça, ponho→ponha,
//   durmo→durma) — across ALL six persons. Suppletive forms (ser→seja) are stored.
// Imperfeito do conjuntivo (-sse): from the eles preterite − "ram".
// Futuro do conjuntivo: from the eles preterite − "am".
const SUBJUNCTIVE_TENSES = ["pres_subj", "imp_subj", "fut_subj"];
const SUBJUNCTIVE_LABELS = {
  pres_subj: "Presente do Conjuntivo",
  imp_subj: "Pretérito Imperfeito do Conjuntivo",
  fut_subj: "Futuro do Conjuntivo",
};

const PRES_SUBJ_ENDINGS = {
  ar: ["e", "es", "e", "emos", "eis", "em"],
  er: ["a", "as", "a", "amos", "ais", "am"],
  ir: ["a", "as", "a", "amos", "ais", "am"],
};

// Orthographic spelling fix before an -e ending (the -ar conjuntivo): keep the
// hard consonant by writing c→qu, g→gu, ç→c.
function fixOrthographic(stem) {
  if (stem.endsWith("ç")) return stem.slice(0, -1) + "c";
  if (stem.endsWith("c")) return stem.slice(0, -1) + "qu";
  if (stem.endsWith("g")) return stem.slice(0, -1) + "gu";
  return stem;
}

// The eu present stem (drops a trailing -o / -ou) — the base of the conjuntivo.
function yoStem(infinitive) {
  const inf = stripSe(infinitive);
  const irr = irrForms(inf);
  let yo = irr && irr.present ? irr.present[0] : regularConjugate(infinitive, "present")[0];
  if (yo.endsWith("ou")) return yo.slice(0, -2);
  if (yo.endsWith("o")) return yo.slice(0, -1);
  return yo;
}

function regularPresentSubj(infinitive) {
  const { ending } = getStemAndEnding(infinitive);
  const stem = stripSe(infinitive).slice(0, -2);
  return PRES_SUBJ_ENDINGS[ending].map((e) => stem + e);
}

function presentSubj(infinitive) {
  const inf = stripSe(infinitive);
  const irr = irrForms(inf);
  const regular = regularPresentSubj(infinitive);
  if (irr && irr.present_subj) return { forms: irr.present_subj, regular };

  const { ending } = getStemAndEnding(infinitive);
  const endings = PRES_SUBJ_ENDINGS[ending];
  let stem = yoStem(infinitive);
  if (ending === "ar") stem = fixOrthographic(stem);
  return { forms: endings.map((e) => stem + e), regular };
}

function imperfectSubj(infinitive) {
  const eles = conjugate(infinitive, "preterite").forms[5];
  const stem = eles.endsWith("ram") ? eles.slice(0, -3) : eles;
  const endings = ["sse", "sses", "sse", "ssemos", "sseis", "ssem"];
  const forms = endings.map((e, i) => (i === 3 || i === 4 ? accentDerivedForm(infinitive, stem) + e : stem + e));
  const { ending } = getStemAndEnding(infinitive);
  const base = stripSe(infinitive).slice(0, -2);
  const regStem = ending === "ar" ? base + "a" : ending === "er" ? base + "e" : base + "i";
  const regular = endings.map((e, i) => (i === 3 || i === 4 ? accentByClass(regStem, ending) + e : regStem + e));
  return { forms, regular };
}

function futureSubj(infinitive) {
  const eles = conjugate(infinitive, "preterite").forms[5];
  const stem = eles.endsWith("am") ? eles.slice(0, -2) : eles;
  const endings = ["", "es", "", "mos", "des", "em"];
  const forms = endings.map((e) => stem + e);
  const regStem = stripSe(infinitive);
  const regular = endings.map((e) => regStem + e);
  return { forms, regular };
}

function conjugateSubj(infinitive, tense) {
  if (tense === "pres_subj") return presentSubj(infinitive);
  if (tense === "imp_subj") return imperfectSubj(infinitive);
  if (tense === "fut_subj") return futureSubj(infinitive);
  return { forms: ["", "", "", "", "", ""], regular: ["", "", "", "", "", ""] };
}

// ============================================================
// PERSONAL (INFLECTED) INFINITIVE — infinitivo pessoal
// ============================================================
// Always morphologically regular: the whole infinitive + [-, es, -, mos, des,
// em], even for irregular verbs (fazer→fazermos). pôr is the lone special case.
function personalInfinitive(infinitive) {
  const inf = stripSe(infinitive);
  const endings = ["", "es", "", "mos", "des", "em"];
  if (inf === "pôr" || inf.endsWith("pôr")) {
    const stem = inf.replace(/pôr$/, "por"); // pôr→por, compôr→compor
    const forms = [inf, stem + "es", inf, stem + "mos", stem + "des", stem + "em"];
    return { forms, regular: forms };
  }
  const forms = endings.map((e) => inf + e);
  return { forms, regular: forms };
}

// ============================================================
// IMPERATIVE / IMPERATIVO
// ============================================================
// tu affirmative   = 3rd-singular present indicative (ser→sê is the exception).
// tu negative      = 2nd-singular present subjunctive.
// você / nós / vocês — affirmative and negative both use the conjuntivo.
// vós affirmative  = 2nd-plural present indicative minus -s (falai, comei, parti).
// vós negative     = 2nd-plural present subjunctive.
const TU_AFF_IRREGULAR = { ser: "sê" };
const VOS_AFF_IRREGULAR = { ser: "sede" };
const IMPERATIVE_ROWS = ["tu", "voce", "nos", "vos", "voces"];
const IMPERATIVE_LABELS = {
  tu: "tu", voce: "você", nos: "nós", vos: "vós", voces: "vocês",
};

function imperative(infinitive) {
  const inf = stripSe(infinitive);
  const { forms: subj, regular: subjReg } = presentSubj(infinitive);
  const { forms: pres, regular: presReg } = conjugate(infinitive, "present");

  let tuAff = pres[2];
  let tuAffReg = presReg[2];
  if (TU_AFF_IRREGULAR[inf]) tuAff = TU_AFF_IRREGULAR[inf];

  let vosAff = pres[4].endsWith("s") ? pres[4].slice(0, -1) : pres[4];
  const vosAffReg = presReg[4].endsWith("s") ? presReg[4].slice(0, -1) : presReg[4];
  if (VOS_AFF_IRREGULAR[inf]) vosAff = VOS_AFF_IRREGULAR[inf];

  const neg = (s) => "não " + s;
  return {
    tu:    { aff: tuAff,   neg: neg(subj[1]), affReg: tuAffReg,   negReg: neg(subjReg[1]) },
    voce:  { aff: subj[2], neg: neg(subj[2]), affReg: subjReg[2], negReg: neg(subjReg[2]) },
    nos:   { aff: subj[3], neg: neg(subj[3]), affReg: subjReg[3], negReg: neg(subjReg[3]) },
    vos:   { aff: vosAff,  neg: neg(subj[4]), affReg: vosAffReg,  negReg: neg(subjReg[4]) },
    voces: { aff: subj[5], neg: neg(subj[5]), affReg: subjReg[5], negReg: neg(subjReg[5]) },
  };
}

// Letter-level diff: returns HTML for `actual` with the characters that don't
// match `regular` (via longest common subsequence) wrapped in
// <span class="irregular">, so the table highlights only the irregular letters.
function diffMarkHTML(actual, regular) {
  const a = Array.from(actual);
  const r = Array.from(regular);
  const m = a.length, n = r.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === r[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const inLcs = new Array(m).fill(false);
  let i = m, j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === r[j - 1]) { inLcs[i - 1] = true; i--; j--; }
    else if (dp[i - 1][j] >= dp[i][j - 1]) i--;
    else j--;
  }
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let out = "", marking = false;
  for (let k = 0; k < m; k++) {
    if (!inLcs[k] && !marking) { out += '<span class="irregular">'; marking = true; }
    else if (inLcs[k] && marking) { out += "</span>"; marking = false; }
    out += esc(a[k]);
  }
  if (marking) out += "</span>";
  return out;
}

// ============================================================
// PARTICIPLES — gerúndio + particípio passado
// ============================================================
// Gerúndio: -ar → -ando, -er → -endo, -ir → -indo (uniform; pôr → pondo).
// Particípio passado: -ar → -ado, -er/-ir → -ido, plus a closed irregular set.
const GER_EXACT = { "pôr": "pondo", vir: "vindo", ter: "tendo", ver: "vendo", ser: "sendo", ir: "indo" };
const PP_EXACT = {
  abrir: "aberto", cobrir: "coberto", descobrir: "descoberto", dizer: "dito",
  escrever: "escrito", fazer: "feito", ganhar: "ganho", gastar: "gasto",
  pagar: "pago", "pôr": "posto", ver: "visto", vir: "vindo", aceitar: "aceite",
  entregar: "entregue", limpar: "limpo", morrer: "morto", soltar: "solto",
  acender: "aceso", prender: "preso", eleger: "eleito", suspender: "suspenso",
  expulsar: "expulso",
};
// Suffix roots whose every prefixed compound shares the irregular participle.
const PP_SUFFIX = [
  ["screver", "scrito"], ["cobrir", "coberto"], ["abrir", "aberto"],
  ["por", "posto"], ["pôr", "posto"], ["fazer", "feito"], ["dizer", "dito"],
  ["ver", "visto"], ["vir", "vindo"],
];

function regularGerund(infinitive) {
  const { stem, ending } = getStemAndEnding(infinitive);
  if (ending === "ar") return stem + "ando";
  if (ending === "ir") return stem + "indo";
  return stem + "endo";
}
function gerund(infinitive) {
  const inf = stripSe(infinitive);
  if (GER_EXACT[inf]) return GER_EXACT[inf];
  if (inf.endsWith("pôr") || inf.endsWith("por")) return inf.replace(/p[oô]r$/, "") + "pondo";
  return regularGerund(infinitive);
}

function regularPastParticiple(infinitive) {
  const { stem, ending } = getStemAndEnding(infinitive);
  if (ending === "ar") return stem + "ado";
  if (/[aeou]$/.test(stem)) return stem + "ído"; // cair→caído, construir→construído
  return stem + "ido";
}
function pastParticiple(infinitive) {
  const inf = stripSe(infinitive);
  if (PP_EXACT[inf]) return PP_EXACT[inf];
  for (const [suf, rep] of PP_SUFFIX) {
    if (inf.endsWith(suf)) return inf.slice(0, -suf.length) + rep;
  }
  return regularPastParticiple(infinitive);
}

function participles(infinitive) {
  return {
    gerundio: gerund(infinitive),
    gerundioReg: regularGerund(infinitive),
    participio: pastParticiple(infinitive),
    participioReg: regularPastParticiple(infinitive),
  };
}

const ALL_VERBS_LIST = buildVerbList();

export {
  ALL_VERBS, ALL_VERBS_LIST, TOP_50_VERBS, ADDITIONAL_VERBS, VOCABULARY, IRREGULAR_FORMS,
  PRONOUNS, TENSES, TENSE_LABELS,
  SUBJUNCTIVE_TENSES, SUBJUNCTIVE_LABELS,
  IMPERATIVE_ROWS, IMPERATIVE_LABELS,
  conjugate, conjugateSubj, imperative, getIrregularFlags, diffMarkHTML, buildVerbList,
  gerund, pastParticiple, participles, personalInfinitive,
};
